import {
    BookAppointmentButton,
    Button,
    CircleAlertIcon,
    Skeleton,
    Typography,
} from '@/components/atoms';
import {
    DirhamIcon,
    NoPropertiesIllustration,
    SlidersIcon,
    UnitReservedConfettiIcon,
} from '@/components/atoms/icons';
import {
    AppointmentActionsPopup,
    AppointmentCanceledDialog,
    BottomSheet,
    CancelAppointmentDialog,
    CardCarousel,
    DashedDivider,
    FullScreenLoader,
    PaymentCardSkeleton,
    ProfileNotificationHeader,
    ReservationCanceledDialog,
} from '@/components/molecules';
import type { UnitInstallmentsMap } from '@/core/api/models/unitInstallments';
import { Toast } from '@/components/molecules/Toast';
import PaymentDetailsDialog from '@/components/organisms/PaymentDetailsDialog';
import {
    appointmentService,
    extractUpcomingAppointment,
    formatAppointmentTimeLabel,
    toAppointmentDateId,
} from '@/core/api/services/appointmentService';
import { bookingService } from '@/core/api/services/bookingService';
import {
    useReservationActions,
    // useSalesAgentContact, // Sales agent contact hidden for now
    useSingleTapAction,
    useTranslation,
    useUIColors,
} from '@/core/hooks';
import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from '@/core/constants/dynatraceCatalog';
import { useCatalogDynatraceView } from '@/core/hooks/useCatalogDynatraceView';
import { useContentfulRevision } from '@/core/hooks/useContentfulRevision';
import { logger } from '@/core/logger';
import {
    trackAdhaAppointmentCardClick,
    trackAdhaReservationCardClick,
    trackCatalogEvent,
    trackReservationSummaryOpen,
} from '@/core/services/trackDynatraceCatalog';
import { useI18nContext } from '@/core/providers/I18nProvider';
import {
    getReservationCtas,
    getReservationToneColor,
    mapLocalReservationSignatureStatus,
    mapSalesforceReservationStatus,
} from '@/core/reservation';
import {
    fetchHomeCoverImage,
    fetchPropertyAssets,
    type PropertyImage,
} from '@/core/services/contentfulClient';
import { refreshContentfulTranslations } from '@/core/services/translationService';
import { useAppStore, type BookedAppointment, type Property } from '@/core/stores/appStore';
import { formatAdhaUnitTitle, getUserFirstName, resolveAdhaProjectName } from '@/core/utils';
import { formatSquareFeet } from '@/core/utils/formatSquareFeet';
import { useProfile } from '@/modules/auth/hooks/useProfile';
import { useSyncProfileDetailsToStore } from '@/modules/auth/hooks/useSyncProfileDetailsToStore';
import { useUnitInstallments } from '@/modules/auth/hooks/useUnitInstallments';
import { formatLongDate } from '@/modules/book-appointment/utils/mockSlots';
import type { PropertiesLayout } from '@/modules/home/hooks/usePropertiesLayout';
import { useVentures } from '@/modules/home/hooks/useVentures';
import { usePasscode } from '@/modules/passcode';
import {
    getAllOverdueInstallments,
    getAllPaidInstallments,
    getAllUpcomingDueInstallments,
    mapInstallmentStatusToPaymentStatus,
} from '@/modules/payments/utils/installmentUtils';
import {
    formatListingCardMessage,
    getReservationVisibleCopy,
} from '@/modules/properties-web/constants/webReservationStatus';
import { useAvailableUnits } from '@/modules/properties-web/hooks/useAvailableUnits';
import { useUnitListingFilterStore } from '@/modules/properties-web/store/unitListingFilterStore';
import {
    defaultSliderMillionsForBedrooms,
    filterUnitsByBedrooms,
} from '@/modules/properties-web/utils/unitListingFilters';
import {
    applyAdhaBookingApiResult,
    buildPendingReservationFromBooking,
} from '@/modules/properties-web/utils/syncAdhaBookingFromApi';
import { resolveIskanLoanAmountAed } from '@/modules/properties-web/utils/iskanLoan';
import { isAdhaProfileActionEnabled } from '@/modules/properties-web/utils/adhaProfileActions';
import { isAppointmentBookingWindowClosed } from '@/modules/book-appointment/utils/slotDates';
import {
    readListingUnitModel,
    resolveListingCardImages,
} from '@/modules/properties-web/utils/pickMixedUnitImages';
import { rememberAdhaHeroImages } from '@/modules/property-details/adhaDetailsProperty';
import { UpcomingAppointmentCard } from '@/modules/property-details/components/UpcomingAppointmentCard';
import { profileService } from '@/modules/auth/services/profileService';
import { formatNamedGreeting, resolveGreetingPrefix } from '@/core/utils/greeting';
import { useQuery } from '@tanstack/react-query';
import { router, useFocusEffect, useLocalSearchParams } from 'expo-router';
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
    Animated,
    ImageSourcePropType,
    FlatList,
    Linking,
    PanResponder,
    Platform,
    RefreshControl,
    ScrollView,
    StyleSheet,
    TouchableOpacity,
    View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { PriceRangeFilterSheet } from './PriceRangeFilterSheet';
import { ProjectListingHeroCard } from './ProjectListingHeroCard';
import { PropertyListingCard } from './PropertyListingCard';
import { PropertyListingCardSkeleton } from './PropertyListingCardSkeleton';
// import { fetchPropertyImagesForProjects } from '@/modules/home/hooks/usePropertyImages'; // superseded by the mock catalogue (useAvailableUnits) for Exclusive Listings images — kept in place instead of deleted

// Sentinel maxPrice sent when the price slider is at 3M+ ("no upper bound").
const MAX_PRICE_SENTINEL = 999999999;
/** Same bounds as web: listing opens at 1.7M, slider ends at 3M+. */
const PRICE_SLIDER_MIN_M = 1;
const PRICE_SLIDER_MAX_M = 3;

// Stable keys for the fixed-length payment-card skeleton placeholders (Sonar S6479).
const PAYMENT_CARD_SKELETON_KEYS = ['payment-card-skeleton-1', 'payment-card-skeleton-2', 'payment-card-skeleton-3'] as const;
const NEXT_PAGE_SKELETON_KEYS = ['listing-next-skeleton-1', 'listing-next-skeleton-2', 'listing-next-skeleton-3'] as const;

/** First non-empty email on the profile, preferring primary. */
function resolveUserEmail(user: {
    primary_email?: string | null;
    secondary_email?: string | null;
    tertiary_email?: string | null;
} | null | undefined): string {
    return user?.primary_email || user?.secondary_email || user?.tertiary_email || '';
}

/** Full slider range (3M+) — API max_price is nine 9s, same as web. */
function isFullPriceRange(maxPriceMillions: number): boolean {
    return maxPriceMillions >= PRICE_SLIDER_MAX_M - 0.05;
}

/**
 * Which empty-listing message to show. Same result as web's PropertiesListScreen:
 * web always sends a max_price, so its "filter" case wins over "no properties"
 * and only a failed request shows a different message.
 */
type ListingEmptyKind = 'fetch_error' | 'filter';

const LISTING_EMPTY_SUBTITLE_KEY = {
    fetch_error: 'propertiesWeb.list.emptyFetchError',
    filter: 'propertiesWeb.list.emptyFilterNoMatch',
} as const;

function resolveListingEmptyKind(isError: boolean): ListingEmptyKind {
    return isError ? 'fetch_error' : 'filter';
}

/** Slider millions → AED max_price for GET /v1/units. Same as web. */
function resolveMaxPriceFilterAed(maxPriceMillions: number): number {
    if (isFullPriceRange(maxPriceMillions)) {
        return MAX_PRICE_SENTINEL;
    }
    return maxPriceMillions * 1_000_000;
}

// Same platform-specific maps URL PropertyDetailsScreenAdhaMobile.tsx's own
// map-pin icon uses (Apple Maps on iOS, geo: on Android, Google Maps
// fallback) — reused here for the project hero's map-pin.
function buildProjectMapsUrl(params: Readonly<{
    platformOs: string;
    latitude: number;
    longitude: number;
}>): string {
    if (params.platformOs === 'ios') {
        return `http://maps.apple.com/?q=${params.latitude},${params.longitude}`;
    }
    if (params.platformOs === 'android') {
        return `geo:${params.latitude},${params.longitude}?q=${params.latitude},${params.longitude}`;
    }
    return `https://www.google.com/maps/search/?api=1&query=${params.latitude},${params.longitude}`;
}

/**
 * Business rule: the reserved unit shall appear first in the listing. Same as
 * web's PropertiesListScreen displayListings: GET /v1/units never returns the
 * currently-booked unit at all, so when it's missing it has to be seeded back
 * in from the sticky snapshot / store, not just reordered.
 */
function resolveDisplayAvailableUnits(
    units: any[],
    reservedPropertyId: string | undefined,
    pendingUnitReservationForListing: any,
    storeAvailableUnitsForListing: any[],
    maxPriceFilterAed: number,
): any[] {
    if (!reservedPropertyId) {
        return units;
    }

    const existingIndex = units.findIndex((u: any) => u.unit_id === reservedPropertyId);
    if (existingIndex === 0) {
        return units;
    }
    if (existingIndex > 0) {
        const reordered = [...units];
        const [reservedListing] = reordered.splice(existingIndex, 1);
        if (!reservedListing) {
            return units;
        }
        reordered.unshift(reservedListing);
        return reordered;
    }

    const seed =
        (pendingUnitReservationForListing?.unitId === reservedPropertyId
            ? pendingUnitReservationForListing.unit
            : undefined) ||
        storeAvailableUnitsForListing.find((u) => u.unit_id === reservedPropertyId);
    if (!seed) {
        return units;
    }
    if (maxPriceFilterAed !== MAX_PRICE_SENTINEL) {
        const unitPrice = Number((seed as any).total_price);
        if (!Number.isFinite(unitPrice) || unitPrice > maxPriceFilterAed) {
            return units;
        }
    }
    return [seed as any, ...units];
}

/**
 * Reservation status/CTA/badge derivation for the reserved-unit card — same
 * Salesforce-status resolution `getReservationCtas` uses internally, so the
 * badge/chip/sheet tone stays consistent with the menu's CTA state.
 */
function resolveReservationDisplayState(params: Readonly<{
    reservedUnit: any;
    appointment: any;
    availableUnits: any[];
    colors: ReturnType<typeof useUIColors>;
    t: ReturnType<typeof useTranslation>['t'];
}>) {
    const { reservedUnit, appointment, availableUnits, colors, t } = params;

    const reservedUnitHasAppointment = Boolean(
        reservedUnit && appointment?.propertyId === reservedUnit.propertyId,
    );

    const reservedUnitSalesforceStatus =
        reservedUnit?.reservationStatus ||
        (availableUnits.find((u) => u.unit_id === reservedUnit?.propertyId) as
            | { reservation_status?: string }
            | undefined
        )?.reservation_status;
    const reservationCtas = getReservationCtas({
        appointmentExists: reservedUnitHasAppointment,
        isLocallyReserved: Boolean(reservedUnit),
        ...(reservedUnitSalesforceStatus ? { salesforceStatus: reservedUnitSalesforceStatus } : {}),
    });
    // Same resolution getReservationCtas uses internally — Salesforce status,
    // falling back to the local "just reserved, SF not synced yet" state —
    // so the badge/chip/sheet tone stays consistent with the menu's CTA state.
    const mappedReservationStatus =
        mapSalesforceReservationStatus(reservedUnitSalesforceStatus) ??
        mapLocalReservationSignatureStatus(Boolean(reservedUnit));
    // "Status Visible Message" — same listing/detail copy pair web uses
    // (src/modules/properties-web/constants/property.label.ts RESERVATION_VISIBLE).
    // Pass the raw Salesforce status through so the Contentful `adhaReservationStatus`
    // per-status key lookup actually runs (falls back to the raw SF status,
    // title-cased, then the local bootstrap) — same resolution web's
    // ReservationStatusMessage/getReservationVisibleCopy already does. Omitting
    // it (as before) meant this always skipped straight to the local bootstrap,
    // never the status-specific CF entry or the raw-status fallback.
    const reservationVisibleCopy = getReservationVisibleCopy(
        mappedReservationStatus,
        t,
        reservedUnitSalesforceStatus,
    );
    // Empty (not a hardcoded local string) while Contentful hasn't settled yet —
    // once ready, listingMessage is always populated (CF per-status entry ->
    // raw SF status, title-cased -> local bootstrap).
    const reservationListingMessage = reservationVisibleCopy
        ? formatListingCardMessage(reservationVisibleCopy) ?? ''
        : '';
    // "Status" row in the Reservation Summary sheet must show the actual
    // status message (same as web's ReservationSummarySheet StatusRow, which
    // renders `copy.listingMessage` via ReservationStatusMessage) — not
    // `.listing`, which is only a short per-tone bucket title (e.g. "In
    // Progress" for every IN_PROGRESS-tone status) meant for badges/chips.
    const reservationStatusLabel = reservationListingMessage;
    const reservationStatusColor = mappedReservationStatus
        ? getReservationToneColor(mappedReservationStatus, colors)
        : colors.importantNoteTitle;
    const reservedUnitListing = availableUnits.find((u) => u.unit_id === reservedUnit?.propertyId);
    const reservedUnitTotalPrice = reservedUnitListing?.total_price;

    return {
        reservationCtas,
        reservationListingMessage,
        reservationStatusLabel,
        reservationStatusColor,
        reservedUnitTotalPrice,
    };
}

type AppStoreState = ReturnType<typeof useAppStore.getState>;

function selectAdhaProjectName(state: AppStoreState): string | undefined {
    return state.adhaProjectDetails?.project_name || state.adhaLoanDetails?.projectName;
}

function selectAdhaProjectId(state: AppStoreState): string | undefined {
    return state.adhaProjectDetails?.project_id || state.adhaLoanDetails?.projectId;
}

function selectAdhaProjectLocation(state: AppStoreState): string {
    return (
        state.adhaProjectDetails?.project_location ||
        state.adhaLoanDetails?.projectLocation ||
        ''
    );
}

// AC-03: "Unit Number, Project Name, Project Location" — e.g. "A1-401, Al Metlaa, Al Ain".

/**
 * Same formatting web's PropertiesListScreen uses for booking `requested_on`/`created_date`.
 * Date and time are formatted separately and joined with ", " — some devices'
 * ICU data joins a combined date+time Intl format with "at" instead of a comma.
 */
function formatBookingRequestedOn(value: string): string {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    const datePart = new Intl.DateTimeFormat('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    }).format(date);
    const timePart = new Intl.DateTimeFormat('en-GB', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    }).format(date).toUpperCase();
    return `${datePart}, ${timePart}`;
}

export function getRelativeDayBadge(
    isoDate: string,
    t: ReturnType<typeof useTranslation>['t'],
): string {
    const [y, m, d] = isoDate.split('-').map(Number);
    if (!y || !m || !d) {
        return '';
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(y, m - 1, d);
    target.setHours(0, 0, 0, 0);
    const diffDays = Math.round(
        (target.getTime() - today.getTime()) / (24 * 60 * 60 * 1000),
    );
    if (diffDays === 0) {
        return t('bookAppointment.today');
    }
    if (diffDays === 1) {
        return t('bookAppointment.tomorrow');
    }
    // Same as web (PropertiesListScreen): only Today/Tomorrow get a badge.
    // Further dates used to show an English-only weekday, even in Arabic.
    return '';
}

export function mapUpcomingToAppStoreAppointment(
    dto: NonNullable<ReturnType<typeof extractUpcomingAppointment>>,
    propertyId: string,
    t: ReturnType<typeof useTranslation>['t'],
    isRTL?: boolean,
): BookedAppointment | null {
    const startTime = String(dto.start_time ?? '').trim();
    const dateIso = startTime
        ? toAppointmentDateId(startTime)
        : String(dto.date ?? dto.appointment_date ?? dto.date_id ?? '').trim();
    const timeLabel = startTime
        ? formatAppointmentTimeLabel(startTime)
        : String(dto.time ?? dto.appointment_time ?? dto.time_id ?? '').trim();

    if (!dateIso && !timeLabel) {
        return null;
    }

    const formattedDate = (() => {
        if (dateIso) {
            return formatLongDate(dateIso, isRTL);
        }
        if (!startTime) {
            return '';
        }
        return new Date(startTime).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            timeZone: 'Asia/Dubai',
        });
    })();

    const badgeLabel = dateIso
        ? getRelativeDayBadge(dateIso, t)
        : t('bookAppointment.upcomingAppointment');

    const appointmentId = String(dto.appointment_id ?? dto.id ?? '').trim();
    const eventId = String(dto.event_id ?? '').trim();

    return {
        propertyId,
        date: formattedDate || dateIso,
        time: timeLabel,
        badgeLabel,
        ...(appointmentId ? { appointmentId } : {}),
        ...(eventId ? { eventId } : {}),
    };
}

function autoSkipPasscodeOnWebIfNeeded(params: Readonly<{
    isAuthenticated: boolean;
    calledRef: React.RefObject<boolean>;
    skipPasscode: (mode: string) => Promise<void>;
}>) {
    if (
        Platform.OS !== 'web' ||
        !params.isAuthenticated ||
        params.calledRef.current
    ) {
        return;
    }

    const checkTokenAndSkip = async () => {
        const authState = useAppStore.getState().auth;

        if (authState.passcodeToken) {
            logger.info('HomeScreen:PasscodeToken already exists, skipping auto-skip passcode call');
            params.calledRef.current = true;
            return;
        }

        if (!authState.token) {
            logger.debug('HomeScreen:Token not available yet, waiting...');
            setTimeout(() => {
                void checkTokenAndSkip();
            }, 100);
            return;
        }

        params.calledRef.current = true;
        logger.info('HomeScreen:Web platform detected, auto-skipping passcode');

        try {
            await params.skipPasscode('login');
            logger.info('HomeScreen:Web auto skip passcode successful');
        } catch (err) {
            logger.error('HomeScreen:Web auto skip passcode failed', {
                error: err,
            });
            // Continue with normal flow even if skip fails
        }
    };

    void checkTokenAndSkip();
}

/** Shared by both skip-passcode outcomes below — was duplicated in .then/.catch. */
async function fetchProfileAndInstallmentsIfAuthenticated(params: Readonly<{
    isAuthenticated: boolean;
    fetchProfile: () => Promise<unknown>;
    fetchAllUnitInstallments: () => Promise<unknown>;
    logContext: string;
}>) {
    if (!params.isAuthenticated) {
        return;
    }
    try {
        await params.fetchProfile();
        logger.debug(`HomeScreen:Profile fetched ${params.logContext}, fetching unit installments`);
        await params.fetchAllUnitInstallments();
    } catch (err) {
        logger.error(`HomeScreen:Failed to fetch profile or installments ${params.logContext}`, { error: err });
    }
}

/**
 * Fetch profile + unit installments on mount — skipped while not authenticated
 * or while the skip-passcode flow is still in flight (it fetches once that
 * completes instead). Returns the mount-guard cleanup for the caller's effect.
 */
function fetchProfileOnMountIfNeeded(params: Readonly<{
    isAuthenticated: boolean;
    skipPasscodeParam: string | undefined;
    skipPasscodeCalledRef: React.RefObject<boolean>;
    fetchProfile: () => Promise<unknown>;
    fetchAllUnitInstallments: () => Promise<unknown>;
}>): (() => void) | undefined {
    if (!params.isAuthenticated) {
        logger.debug('HomeScreen:Skip profile fetch - user not authenticated');
        return undefined;
    }

    if (params.skipPasscodeParam === 'true' && !params.skipPasscodeCalledRef.current) {
        logger.debug('HomeScreen:Skip profile fetch - waiting for skip passcode');
        return undefined;
    }

    let isMounted = true;

    params.fetchProfile()
        .then(() => {
            if (!isMounted) return undefined;
            logger.debug('HomeScreen:Profile fetched, fetching unit installments');
            return params.fetchAllUnitInstallments();
        })
        .catch((err) => {
            if (!isMounted) return;
            logger.error('HomeScreen:Failed to fetch profile or installments', { error: err });
        });

    return () => {
        isMounted = false;
    };
}

/** Payment-cards CTA nav params — pre-selects the tapped card's payment IDs when known. */
function resolvePaymentCardNavParams(
    category: 'overdue' | 'upcomingDue',
    propertyId: string,
    overduePaymentIdsMap: Map<string, string[]>,
    upcomingDuePaymentIdsMap: Map<string, string[]>,
): { tab: string; selectedPaymentIds?: string } {
    const paymentIds = category === 'overdue'
        ? overduePaymentIdsMap.get(propertyId) || []
        : upcomingDuePaymentIdsMap.get(propertyId) || [];
    return paymentIds.length > 0
        ? { tab: 'upcoming', selectedPaymentIds: paymentIds.join(',') }
        : { tab: 'upcoming' };
}

/** Same Contentful lookup as property details: fields.projectId, not project name. */
async function fetchAdhaHomeContentfulAssets(params: Readonly<{
    projectId: string | undefined;
    projectName: string | undefined;
    locale: 'en' | 'ar';
    setContentfulImages: (images: ImageSourcePropType[]) => void;
    setContentfulUnitImageLookup: (
        lookup: Record<string, PropertyImage[]> | null,
    ) => void;
    setContentfulName: (name: string | undefined) => void;
    /** Pull-to-refresh: bypass the Contentful cache and keep current content on failure. */
    forceRefresh?: boolean;
}>): Promise<void> {
    logger.debug('HomeScreenMobileAdhaMobile:Contentful lookup', {
        projectId: params.projectId,
        projectName: params.projectName,
        locale: params.locale,
    });
    if (!params.projectId) {
        params.setContentfulImages([]);
        params.setContentfulUnitImageLookup(null);
        params.setContentfulName(undefined);
        return;
    }

    try {
        const assets = await fetchPropertyAssets(params.projectId, {
            maxWidth: 600,
            locale: params.locale,
            forceRefresh: params.forceRefresh === true,
        });
        const sources = assets.images.map((img) => ({ uri: img.url }));
        logger.debug('HomeScreenMobileAdhaMobile:Contentful images applied', {
            projectId: params.projectId,
            imageCount: sources.length,
            unitImageCount: assets.unitImages?.length ?? 0,
            hasName: !!assets.name,
        });
        params.setContentfulImages(sources);
        // Same lookup as web's PropertiesListScreen: model gallery, then unitImages,
        // then propertyImages. Null until this fetch succeeds so cards stay on the
        // placeholder instead of a fallback set.
        params.setContentfulUnitImageLookup(assets.unitImagesByUnitModel ?? {});
        params.setContentfulName(assets.name?.trim() || undefined);
    } catch (error) {
        logger.warn('HomeScreenMobile', 'Failed to fetch exclusive listing images', { error });
        if (params.forceRefresh) {
            // A failed refresh keeps what is already on screen.
            return;
        }
        params.setContentfulImages([]);
        params.setContentfulUnitImageLookup(null);
        params.setContentfulName(undefined);
    }
}

/** `params.skipPasscode==='true'` handler for the post-login redirect — calls skip-passcode, then
 * fetches profile/installments either way (success or failure), matching the effect's prior behavior. */
async function handleSkipPasscodeFlag(params: Readonly<{
    calledRef: React.RefObject<boolean>;
    skipPasscode: (mode: string) => Promise<void>;
    isAuthenticated: boolean;
    fetchProfile: () => Promise<unknown>;
    fetchAllUnitInstallments: () => Promise<unknown>;
}>) {
    params.calledRef.current = true;
    logger.info('HomeScreen:Skip passcode flag detected, calling skip passcode API');

    let logContext = 'after skip passcode';
    try {
        await params.skipPasscode('login');
        logger.info('HomeScreen:Skip passcode successful, proceeding with normal flow');
    } catch (err) {
        logger.error('HomeScreen:Failed to skip passcode', { error: err });
        // Still proceed with normal flow even if skip fails.
        logContext = 'after skip passcode failure';
    }

    await fetchProfileAndInstallmentsIfAuthenticated({
        isAuthenticated: params.isAuthenticated,
        fetchProfile: params.fetchProfile,
        fetchAllUnitInstallments: params.fetchAllUnitInstallments,
        logContext,
    });
}

// Memoized: re-renders (and re-runs the per-unit Contentful image resolution
// below) only when its own props change — not just because a sibling state
// (e.g. `reservationSummaryVisible`) toggled in the parent HomeScreenMobileAdhaMobile.
// That toggle used to re-run this whole per-unit computation for every listed
// unit, which is what made opening the reservation sheet feel stuck.
const AdhaUnitListingRow = React.memo(function AdhaUnitListingRow(props: Readonly<{
    unit: any;
    adhaProjectName: string;
    adhaProjectLocation: string;
    contentfulName?: string;
    contentfulUnitImageLookup: Record<string, PropertyImage[]> | null;
}>) {
    const runSingleTap = useSingleTapAction();
    const project = resolveAdhaProjectName(
        props.unit.project_name || props.adhaProjectName,
        props.contentfulName,
    );
    const unitTitle = formatAdhaUnitTitle(
        props.unit.unit_number,
        project,
        props.adhaProjectLocation,
    );
    // Resolved only while this row is mounted (FlatList window), not for every
    // catalogue unit. List rows show a single photo.
    const cardImages = props.contentfulUnitImageLookup
        ? resolveListingCardImages(
            props.contentfulUnitImageLookup,
            readListingUnitModel(props.unit),
            props.unit.unit_id,
        ).slice(0, 1)
        : [];

    return (
        <PropertyListingCard
            layout="list"
            images={cardImages}
            unitTitle={unitTitle}
            bedrooms={Number.parseInt(String(props.unit.number_of_bedrooms ?? ''), 10)}
            bathrooms={
                props.unit.number_of_bathrooms != null
                    ? Number(props.unit.number_of_bathrooms)
                    : null
            }
            areaSqFt={props.unit.total_area}
            areaSqFtDisplay={formatSquareFeet(props.unit.total_area)}
            price={props.unit.total_price}
            handoverDate={props.unit.handover_date ?? null}
            onPress={() => {
                trackCatalogEvent({
                    screen: ALMETLAA_SCREEN.CATALOGUE,
                    actionName: ALMETLAA_ACTION.CATALOGUE.PROJECT_CARD_CLICK,
                    eventType: 'action',
                    properties: { unitId: props.unit.unit_id },
                });
                runSingleTap(() => {
                    rememberAdhaHeroImages(props.unit.unit_id, cardImages);
                    router.push({
                        pathname: '/(auth)/property-details',
                        params: { propertyId: props.unit.unit_id },
                    } as any);
                });
            }}
        />
    );
});

const ExclusiveListingsList = React.memo(function ExclusiveListingsList(props: Readonly<{
    availableUnitsLoading: boolean;
    isFetchingFirstPage: boolean;
    isFetchingNextPage: boolean;
    availableUnits: any[];
    adhaProjectName: string;
    adhaProjectLocation: string;
    contentfulImages: ImageSourcePropType[];
    contentfulUnitImageLookup: Record<string, PropertyImage[]> | null;
    contentfulName?: string;
    projectId: string;
    emptyKind: ListingEmptyKind;
    t: ReturnType<typeof useTranslation>['t'];
}>) {
    // Rapid repeat taps on a card would otherwise push the same screen several times.
    const runSingleTap = useSingleTapAction();

    if (props.availableUnitsLoading || props.isFetchingFirstPage) {
        return (
            <View style={styles.listingsList}>
                {/* First item mirrors the project hero (big carousel card); the
                    rest mirror the compact unit rows below it. */}
                <PropertyListingCardSkeleton layout="grid" />
                <PropertyListingCardSkeleton layout="list" />
            </View>
        );
    }

    const heroProjectName = resolveAdhaProjectName(
        props.adhaProjectName,
        props.contentfulName,
    );
    const hasUnits = props.availableUnits.length > 0;
    // Same stand-in used elsewhere on this screen (e.g. the Book Appointment
    // FAB) when a single unit id is needed to represent the whole project —
    // here, as the source of the project's map coordinates.
    const primaryUnit = hasUnits ? props.availableUnits[0] : undefined;
    // Same "only show when the API actually returned it" guard as
    // PropertyDetailsScreenAdhaMobile.tsx's own map-pin icon — lat/long
    // default to 0 when the API has no coordinates.
    const hasValidCoordinates =
        Boolean(primaryUnit?.latitude) && Boolean(primaryUnit?.longitude);
    // Like web's project panel, the project stays visible when no units match;
    // only the unit list underneath turns into the empty state.
    const showProjectHero = hasUnits || Boolean(heroProjectName || props.projectId);
    // Built outside the JSX so the map-pin prop is not a ternary nested inside
    // the hero's own conditional (Sonar S3358).
    let heroMapProps: { onMapPress?: () => void } = {};
    if (hasValidCoordinates && primaryUnit) {
        heroMapProps = {
            onMapPress: () => {
                const mapUrl = buildProjectMapsUrl({
                    platformOs: Platform.OS,
                    latitude: primaryUnit.latitude,
                    longitude: primaryUnit.longitude,
                });
                runSingleTap(() => {
                    Linking.openURL(mapUrl).catch((err) => {
                        logger.error(
                            'HomeScreen:Failed to open project maps',
                            err,
                        );
                    });
                });
            },
        };
    }

    return (
        <View style={styles.listingsList}>
            {showProjectHero && (
                // Project-level hero (image carousel + "X Available Units" +
                // project name/location) shown once, per the new design —
                // the per-unit cards below it use the compact row layout.
                <ProjectListingHeroCard
                    images={props.contentfulImages}
                    projectName={heroProjectName}
                    location={props.adhaProjectLocation}
                    availableUnitsCount={props.availableUnits.length}
                    {...heroMapProps}
                    onPress={() => {
                        const projectId =
                            primaryUnit?.project_id || primaryUnit?.projectId || props.projectId;
                        trackCatalogEvent({
                            screen: ALMETLAA_SCREEN.CATALOGUE,
                            actionName: ALMETLAA_ACTION.CATALOGUE.PROJECT_CARD_CLICK,
                            eventType: 'action',
                            ...(projectId ? { properties: { projectId: String(projectId) } } : {}),
                        });
                        runSingleTap(() => router.push('/(auth)/project-details' as any));
                    }}
                />
            )}
            {!hasUnits && (
                <View style={styles.listingsEmptyState}>
                    <NoPropertiesIllustration />
                    <Typography
                        variant="headline"
                        color="primary"
                        style={styles.listingsEmptyTitle}
                    >
                        {props.t('properties.noPropertiesYetTitle')}
                    </Typography>
                    <Typography
                        variant="body"
                        color="tertiary"
                        style={styles.listingsEmptySubtitle}
                    >
                        {props.t(LISTING_EMPTY_SUBTITLE_KEY[props.emptyKind])}
                    </Typography>
                </View>
            )}
        </View>
    );
});

const LISTING_THUMB_WIDTH = 4;
const LISTING_THUMB_INSET = 3;
const LISTING_THUMB_MIN_HEIGHT = 24;
/** Wider than the painted bar so a finger can grab it. */
const LISTING_THUMB_HIT_WIDTH = 28;

interface ListingScrollThumbHandle {
    setViewport: (height: number) => void;
    setContent: (height: number) => void;
    setOffset: (offset: number) => void;
}

function listingThumbGeometry(viewport: number, content: number, offset: number) {
    const scrollable = viewport > 0 && content > viewport + 1;
    const track = Math.max(0, viewport - LISTING_THUMB_INSET * 2);
    const thumbHeight = scrollable
        ? Math.min(track, Math.max(LISTING_THUMB_MIN_HEIGHT, (viewport / content) * track))
        : 0;
    const maxOffset = Math.max(0, content - viewport);
    const travel = Math.max(1, track - thumbHeight);
    const progress = maxOffset <= 0 ? 0 : Math.min(1, Math.max(0, offset / maxOffset));
    const thumbTop = LISTING_THUMB_INSET + progress * Math.max(0, track - thumbHeight);
    return { scrollable, thumbHeight, maxOffset, travel, thumbTop };
}

/** Thin bar that stays on the unit list and scrolls it when dragged. */
const ListingScrollThumb = React.forwardRef<
    ListingScrollThumbHandle,
    { onScrollTo: (offset: number) => void }
>(function ListingScrollThumb({ onScrollTo }, ref) {
    const colors = useUIColors();
    const metrics = useRef({ viewport: 0, content: 0, offset: 0, dragging: false });
    const dragOrigin = useRef({ y: 0, offset: 0 });
    const onScrollToRef = useRef(onScrollTo);
    onScrollToRef.current = onScrollTo;
    const top = useRef(new Animated.Value(0)).current;
    const height = useRef(new Animated.Value(0)).current;
    const opacity = useRef(new Animated.Value(0)).current;

    const paint = useCallback(() => {
        const { viewport, content, offset, dragging } = metrics.current;
        const geometry = listingThumbGeometry(viewport, content, offset);
        if (!geometry.scrollable) {
            opacity.setValue(0);
            return;
        }
        top.setValue(geometry.thumbTop);
        height.setValue(geometry.thumbHeight);
        opacity.setValue(dragging ? 0.7 : 0.45);
    }, [height, opacity, top]);

    const scrollFromThumbTop = useCallback((thumbTop: number) => {
        const { viewport, content } = metrics.current;
        const geometry = listingThumbGeometry(viewport, content, metrics.current.offset);
        if (!geometry.scrollable) {
            return;
        }
        const nextTop = Math.min(
            Math.max(LISTING_THUMB_INSET, thumbTop),
            LISTING_THUMB_INSET + geometry.travel,
        );
        const nextOffset = ((nextTop - LISTING_THUMB_INSET) / geometry.travel) * geometry.maxOffset;
        metrics.current.offset = nextOffset;
        paint();
        onScrollToRef.current(nextOffset);
    }, [paint]);

    const panResponder = useRef(PanResponder.create({
        onStartShouldSetPanResponder: () => listingThumbGeometry(
            metrics.current.viewport,
            metrics.current.content,
            metrics.current.offset,
        ).scrollable,
        onMoveShouldSetPanResponder: () => listingThumbGeometry(
            metrics.current.viewport,
            metrics.current.content,
            metrics.current.offset,
        ).scrollable,
        onPanResponderGrant: (event) => {
            const geometry = listingThumbGeometry(
                metrics.current.viewport,
                metrics.current.content,
                metrics.current.offset,
            );
            const fingerY = event.nativeEvent.locationY;
            const onThumb = fingerY >= geometry.thumbTop
                && fingerY <= geometry.thumbTop + geometry.thumbHeight;
            metrics.current.dragging = true;
            if (!onThumb) {
                scrollFromThumbTop(fingerY - geometry.thumbHeight / 2);
            } else {
                paint();
            }
            dragOrigin.current = {
                y: event.nativeEvent.pageY,
                offset: metrics.current.offset,
            };
        },
        onPanResponderMove: (event) => {
            const geometry = listingThumbGeometry(
                metrics.current.viewport,
                metrics.current.content,
                dragOrigin.current.offset,
            );
            if (!geometry.scrollable) {
                return;
            }
            const dy = event.nativeEvent.pageY - dragOrigin.current.y;
            const nextOffset = dragOrigin.current.offset + (dy / geometry.travel) * geometry.maxOffset;
            metrics.current.offset = Math.min(geometry.maxOffset, Math.max(0, nextOffset));
            paint();
            onScrollToRef.current(metrics.current.offset);
        },
        onPanResponderRelease: () => {
            metrics.current.dragging = false;
            paint();
        },
        onPanResponderTerminate: () => {
            metrics.current.dragging = false;
            paint();
        },
    })).current;

    useImperativeHandle(ref, () => ({
        setViewport(value: number) {
            metrics.current.viewport = value;
            paint();
        },
        setContent(value: number) {
            metrics.current.content = value;
            paint();
        },
        setOffset(value: number) {
            if (metrics.current.dragging) {
                return;
            }
            metrics.current.offset = value;
            paint();
        },
    }), [paint]);

    return (
        <View
            style={styles.listingScrollHit}
            accessibilityRole="adjustable"
            {...panResponder.panHandlers}
        >
            <Animated.View
                pointerEvents="none"
                style={[
                    styles.listingScrollThumb,
                    {
                        backgroundColor: colors.fontPrimary,
                        top,
                        height,
                        opacity,
                    },
                ]}
            />
        </View>
    );
});

function ExclusiveListingsSection(props: Readonly<{
    upcomingAppointment: any;
    reservedUnit: any;
    reservationListingMessage: string;
    onUpcomingMenuPress: () => void;
    onReservedCardPress: () => void;
    carouselPaused: boolean;
    t: ReturnType<typeof useTranslation>['t'];
    availableUnitsLoading: boolean;
    isFetchingFirstPage: boolean;
    isFetchingNextPage: boolean;
    availableUnits: any[];
    adhaProjectName: string;
    adhaProjectLocation: string;
    contentfulImages: ImageSourcePropType[];
    contentfulUnitImageLookup: Record<string, PropertyImage[]> | null;
    contentfulName?: string;
    projectId: string;
    emptyKind: ListingEmptyKind;
    refreshControl: React.ReactElement;
    contentPaddingBottom: number;
}>) {
    // const salesAgent = useSalesAgentContact(); // Sales agent contact hidden for now
    const hasAnyExclusiveListing = Boolean(
        props.upcomingAppointment || props.reservedUnit,
    );
    const showUnitRows =
        !props.availableUnitsLoading &&
        !props.isFetchingFirstPage &&
        props.availableUnits.length > 0;
    const listingThumbRef = useRef<ListingScrollThumbHandle>(null);
    const listRef = useRef<FlatList>(null);
    const scrollListTo = useCallback((offset: number) => {
        listRef.current?.scrollToOffset({ offset, animated: false });
    }, []);

    return (
        <View style={styles.scrollView}>
        <FlatList
            ref={listRef}
            style={styles.scrollView}
            contentContainerStyle={[
                styles.scrollContent,
                { paddingBottom: props.contentPaddingBottom },
            ]}
            data={showUnitRows ? props.availableUnits : []}
            keyExtractor={(unit) => unit.unit_id}
            showsVerticalScrollIndicator={false}
            scrollEventThrottle={16}
            onScroll={(event) => {
                listingThumbRef.current?.setOffset(event.nativeEvent.contentOffset.y);
            }}
            onLayout={(event) => {
                listingThumbRef.current?.setViewport(event.nativeEvent.layout.height);
            }}
            onContentSizeChange={(_width, contentHeight) => {
                listingThumbRef.current?.setContent(contentHeight);
            }}
            contentInsetAdjustmentBehavior="automatic"
            refreshControl={props.refreshControl}
            initialNumToRender={6}
            maxToRenderPerBatch={4}
            windowSize={5}
            updateCellsBatchingPeriod={50}
            removeClippedSubviews={Platform.OS === 'android'}
            ListHeaderComponent={
                <View style={[styles.section, styles.listingsSection]}>
                    {hasAnyExclusiveListing && (
                        <CardCarousel paused={props.carouselPaused}>
                            {props.upcomingAppointment && (
                                <UpcomingAppointmentCard
                                    date={props.upcomingAppointment.date}
                                    time={props.upcomingAppointment.time}
                                    /* Sales agent contact hidden for now
                                    agentName={salesAgent.name || undefined}
                                    agentPhone={salesAgent.phone || undefined}
                                    */
                                    badgeLabel={props.upcomingAppointment.badgeLabel}
                                    onMenuPress={props.onUpcomingMenuPress}
                                />
                            )}
                            {props.reservedUnit && (
                                <UpcomingAppointmentCard
                                    title={props.t('reserveUnit.unitReservedTitle')}
                                    name={props.reservedUnit.unitLabel}
                                    note={props.reservationListingMessage}
                                    icon={<UnitReservedConfettiIcon size={44} />}
                                    onPress={props.onReservedCardPress}
                                />
                            )}
                        </CardCarousel>
                    )}
                    <ExclusiveListingsList
                        availableUnitsLoading={props.availableUnitsLoading}
                        isFetchingFirstPage={props.isFetchingFirstPage}
                        isFetchingNextPage={props.isFetchingNextPage}
                        availableUnits={props.availableUnits}
                        adhaProjectName={props.adhaProjectName}
                        adhaProjectLocation={props.adhaProjectLocation}
                        contentfulImages={props.contentfulImages}
                        contentfulUnitImageLookup={props.contentfulUnitImageLookup}
                        contentfulName={props.contentfulName}
                        projectId={props.projectId}
                        emptyKind={props.emptyKind}
                        t={props.t}
                    />
                </View>
            }
            renderItem={({ item }) => (
                <View style={styles.listingRow}>
                    <AdhaUnitListingRow
                        unit={item}
                        adhaProjectName={props.adhaProjectName}
                        adhaProjectLocation={props.adhaProjectLocation}
                        contentfulUnitImageLookup={props.contentfulUnitImageLookup}
                        {...(props.contentfulName ? { contentfulName: props.contentfulName } : {})}
                    />
                </View>
            )}
            ListFooterComponent={
                props.isFetchingNextPage ? (
                    <View style={[styles.listingsList, styles.listingRow]}>
                        {NEXT_PAGE_SKELETON_KEYS.map((skeletonKey) => (
                            <PropertyListingCardSkeleton key={skeletonKey} layout="list" />
                        ))}
                    </View>
                ) : null
            }
        />
        <ListingScrollThumb ref={listingThumbRef} onScrollTo={scrollListTo} />
        </View>
    );
}

// Same rule as web's isInactiveReservationStatus (syncAdhaBookingFromApi.ts) —
// a cancelled/rejected status must clear state, never be shown as active.
function isInactiveReservationStatus(status: string): boolean {
    const normalized = status.trim().toLowerCase();
    return normalized.includes('cancel') || normalized.includes('reject');
}

function isReservedUnitInSync(
    current: ReturnType<typeof useAppStore.getState>['reservedUnit'],
    next: Readonly<{
        propertyId: string;
        parentId: string | undefined;
        reservationStatus: string | undefined;
        requestedOn: string | undefined;
        unitLabel: string;
    }>,
): boolean {
    return (
        current?.propertyId === next.propertyId &&
        current.parentId === next.parentId &&
        current.reservationStatus === next.reservationStatus &&
        current.requestedOn === next.requestedOn &&
        current.unitLabel === next.unitLabel
    );
}

function queueReservationCancelConfirm(params: Readonly<{
    isIos: boolean;
    pendingSheetRef: React.RefObject<(() => void) | null>;
    hideSummary: () => void;
    showConfirm: () => void;
}>): void {
    if (params.isIos) {
        params.pendingSheetRef.current = params.showConfirm;
        params.hideSummary();
        return;
    }
    params.hideSummary();
    params.showConfirm();
}

async function confirmCancelAppointmentOnServer(params: Readonly<{
    appointment: any;
    primaryUnitId: string | undefined;
    setCancelDialogVisible: (value: boolean) => void;
    setCancelSuccessVisible: (value: boolean) => void;
    setIsCanceling: (value: boolean) => void;
}>) {
    params.setCancelDialogVisible(false);
    params.setIsCanceling(true);

    let eventId = params.appointment?.eventId?.trim() || '';

    if (!eventId) {
        try {
            logger.info(
                'HomeScreenMobileAdhaMobile:API getUpcoming (resolve eventId) — calling',
                { primaryUnitId: params.primaryUnitId },
            );
            const response = await appointmentService.getUpcoming({
                ...(params.primaryUnitId
                    ? { unitId: params.primaryUnitId }
                    : {}),
            });
            logger.info(
                'HomeScreenMobileAdhaMobile:API getUpcoming (resolve eventId) — success',
                { response },
            );
            const dto = extractUpcomingAppointment(response);
            const rawEventId = dto?.event_id;
            eventId =
                typeof rawEventId === 'string' ? rawEventId.trim() : '';
        } catch (error) {
            logger.warn(
                'HomeScreenMobileAdhaMobile:cancel upcoming refresh failed',
                error,
            );
        }
    }

    if (!eventId) {
        logger.warn(
            'HomeScreenMobileAdhaMobile:cancel aborted — no appointment event_id',
        );
        params.setIsCanceling(false);
        return;
    }

    try {
        await appointmentService.updateAppointment({
            action_type: 'Cancelled',
            event_id: eventId,
            appointment_slot_id: '',
            reason: 'Cancelled by customer',
        });
        useAppStore.getState().clearAppointment();
        params.setCancelSuccessVisible(true);
    } catch (error) {
        logger.warn('HomeScreenMobileAdhaMobile:confirmCancel failed', error);
    } finally {
        params.setIsCanceling(false);
    }
}

function useHomeScreenBookingSync(
    customerId: string | null | undefined,
    adhaProjectName: string,
    adhaProjectLocation: string,
    t: ReturnType<typeof useTranslation>['t'],
    contentfulName?: string,
) {
    const bookingQuery = useQuery({
        queryKey: ['adhaBooking', customerId],
        queryFn: () => bookingService.getBookingDetails(customerId),
        enabled: !!customerId,
        retry: false,
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false,
    });

    const profileQuery = useQuery({
        queryKey: ['adhaProfileDetails', customerId],
        queryFn: () => profileService.getProfileDetails(),
        enabled: !!customerId,
        retry: false,
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false,
    });

    useSyncProfileDetailsToStore(profileQuery.data, profileQuery.isSuccess);

    useEffect(() => {
        if (bookingQuery.error) {
            logger.warn('HomeScreenMobileAdhaMobile:API getBookingDetails — failed', bookingQuery.error);
        }
    }, [bookingQuery.error]);

    // Mirrors reservedUnit (mobile-only field) to whatever pendingUnitReservation
    // (shared with web) currently holds, so Home's card stays in sync with
    // whichever source (booking or profile overlay) last wrote it.
    const syncReservedUnitFromPending = useCallback(() => {
        const pending = useAppStore.getState().pendingUnitReservation;
        const current = useAppStore.getState().reservedUnit;
        if (!pending) {
            if (current) {
                useAppStore.getState().clearReservedUnit();
            }
            return;
        }
        if (
            isReservedUnitInSync(current, {
                propertyId: pending.unitId,
                parentId: pending.parentId,
                reservationStatus: pending.reservationStatus,
                requestedOn: pending.requestedOn || undefined,
                unitLabel: pending.unitTitle,
            })
        ) {
            return;
        }
        useAppStore.getState().setReservedUnit({
            propertyId: pending.unitId,
            unitLabel: pending.unitTitle,
            image: current?.propertyId === pending.unitId ? (current.image ?? null) : null,
            badgeLabel: t('reserveUnit.pendingBadge'),
            ...(pending.parentId ? { parentId: pending.parentId } : {}),
            ...(pending.reservationStatus ? { reservationStatus: pending.reservationStatus } : {}),
            ...(pending.requestedOn ? { requestedOn: pending.requestedOn } : {}),
        });
    }, [t]);

    useEffect(() => {
        if (!customerId || !bookingQuery.isFetched) {
            return;
        }

        // Same precedence as web's useAdhaReservationStatus: getBookingDetails
        // (already filters cancelled/rejected) decides whether a reservation
        // exists at all; profile-details `current_status` only overlays the
        // status label on an ALREADY-confirmed active booking.
        const resolveUnitTitle = (unit: { unit_number?: string; project_name?: string }) =>
            formatAdhaUnitTitle(
                unit.unit_number || '',
                resolveAdhaProjectName(unit.project_name || adhaProjectName, contentfulName),
                adhaProjectLocation,
            );

        if (bookingQuery.isError) {
            logger.warn('HomeScreenMobileAdhaMobile:getBookingDetails errored — clearing', bookingQuery.error);
            useAppStore.getState().clearAdhaWebBookingState();
            syncReservedUnitFromPending();
            return;
        }

        const hasActiveBooking = applyAdhaBookingApiResult({
            response: bookingQuery.data,
            availableUnits: useAppStore.getState().availableUnits,
            resolveUnitTitle,
            formatRequestedOn: formatBookingRequestedOn,
            setPendingUnitReservation: useAppStore.getState().setPendingUnitReservation,
            clearAdhaWebBookingState: useAppStore.getState().clearAdhaWebBookingState,
            getPreviousPending: () => useAppStore.getState().pendingUnitReservation,
            upsertAvailableUnitFirst: useAppStore.getState().upsertAvailableUnitFirst,
        });

        if (!hasActiveBooking) {
            syncReservedUnitFromPending();
            return;
        }

        // Overlay: only trust profile's status when it (a) is for the
        // SAME unit the booking just confirmed active, and (b) isn't itself a
        // cancelled/rejected value — two checks web's own version is missing.
        // Field read depends on login's convertedLead: true → `status`, false → `current_status`.
        const profileData = profileQuery.isSuccess ? profileQuery.data : null;
        const activeUnitId = useAppStore.getState().pendingUnitReservation?.unitId;
        const convertedLead = useAppStore.getState().convertedLead;
        const overlayStatus = convertedLead ? profileData?.status : profileData?.current_status;
        if (
            overlayStatus &&
            profileData?.unit_id &&
            profileData.unit_id === activeUnitId &&
            !isInactiveReservationStatus(overlayStatus)
        ) {
            logger.info('HomeScreenMobileAdhaMobile:profile-details — overlaying status', {
                unitId: profileData.unit_id,
                convertedLead,
                overlayStatus,
            });
            const overlaid = buildPendingReservationFromBooking(
                {
                    unitId: profileData.unit_id,
                    reservationStatus: overlayStatus,
                    parentId: '',
                    requestedOnRaw: profileData.booking_date || profileData.booking_form_signed_date || '',
                    unitNumberHint: profileData.unit_name || profileData.unit_id,
                },
                {
                    availableUnits: useAppStore.getState().availableUnits,
                    previous: useAppStore.getState().pendingUnitReservation,
                    resolveUnitTitle,
                    formatRequestedOn: formatBookingRequestedOn,
                },
            );
            useAppStore.getState().setPendingUnitReservation(overlaid);
        }

        syncReservedUnitFromPending();
    }, [
        profileQuery.data,
        profileQuery.isSuccess,
        bookingQuery.data,
        bookingQuery.isError,
        bookingQuery.isFetched,
        bookingQuery.error,
        customerId,
        adhaProjectName,
        adhaProjectLocation,
        contentfulName,
        syncReservedUnitFromPending,
    ]);

    return { bookingQuery, profileQuery };
}

// A user has at most one active appointment total (BRD: "only one active
// appointment at a time") — query without a unit filter so this reflects
// that appointment (or its absence, e.g. after a cancellation from web)
// regardless of which unit it's for, instead of only recognizing it when it
// happens to match primaryUnitId (the first catalogue unit, not necessarily
// the one the appointment was booked for). Shared by the mount-sync effect,
// pull-to-refresh, and other ADHA screens' own focus-refresh so cancelling
// or updating it elsewhere (web, another screen) is always reflected.
export async function refreshUpcomingAppointment(
    primaryUnitId: string,
    t: ReturnType<typeof useTranslation>['t'],
    isRTL?: boolean,
): Promise<void> {
    try {
        logger.info('HomeScreenMobileAdhaMobile:API getUpcoming (refresh) — calling');
        const response = await appointmentService.getUpcoming();
        logger.info('HomeScreenMobileAdhaMobile:API getUpcoming (refresh) — success', { response });

        const dto = extractUpcomingAppointment(response);
        if (!dto) {
            useAppStore.getState().clearAppointment();
            return;
        }

        const mapped = mapUpcomingToAppStoreAppointment(
            dto,
            dto.unit_id || dto.property_id || primaryUnitId,
            t,
            isRTL,
        );
        if (mapped) {
            useAppStore.getState().setAppointment(mapped);
        } else {
            useAppStore.getState().clearAppointment();
        }
    } catch (error) {
        logger.warn('HomeScreenMobileAdhaMobile:API getUpcoming (refresh) — failed', error);
    }
}

function useHomeScreenUpcomingSync(
    isAuthenticated: boolean,
    primaryUnitId: string,
    t: ReturnType<typeof useTranslation>['t'],
    isRTL: boolean,
) {
    useEffect(() => {
        if (!isAuthenticated || !primaryUnitId) {
            return;
        }
        void refreshUpcomingAppointment(primaryUnitId, t, isRTL);
    }, [isAuthenticated, primaryUnitId, t, isRTL]);
}

function useHomeScreenPaymentData(
    unitInstallments: any[],
    properties: any[],
) {
    const overduePaymentIdsMap = useMemo(() => {
        const overdueItems = getAllOverdueInstallments(unitInstallments, properties);
        const map = new Map<string, string[]>();

        overdueItems.forEach((item) => {
            const existing = map.get(item.propertyId) || [];
            existing.push(item.id);
            map.set(item.propertyId, existing);
        });

        return map;
    }, [unitInstallments, properties]);

    const overduePaymentCards = useMemo(() => {
        const overdueItems = getAllOverdueInstallments(unitInstallments, properties);
        const propertyMap = new Map<string, { propertyName: string; amount: number; count: number }>();

        overdueItems.forEach((item) => {
            const existing = propertyMap.get(item.propertyId) || {
                propertyName: item.propertyName,
                amount: 0,
                count: 0,
            };
            existing.amount += item.due_amount || 0;
            existing.count += 1;
            propertyMap.set(item.propertyId, existing);
        });

        return Array.from(propertyMap.entries()).map(([propertyId, data]) => ({
            id: propertyId,
            propertyName: data.propertyName,
            amount: data.amount,
            paymentCount: data.count,
        }));
    }, [unitInstallments, properties]);

    const upcomingDuePaymentIdsMap = useMemo(() => {
        const upcomingItems = getAllUpcomingDueInstallments(unitInstallments, properties);
        const map = new Map<string, string[]>();

        upcomingItems.forEach((item) => {
            const existing = map.get(item.propertyId) || [];
            existing.push(item.id);
            map.set(item.propertyId, existing);
        });

        return map;
    }, [unitInstallments, properties]);

    const upcomingDuePaymentCards = useMemo(() => {
        const upcomingItems = getAllUpcomingDueInstallments(unitInstallments, properties);
        const propertyMap = new Map<string, { propertyName: string; amount: number; count: number }>();

        upcomingItems.forEach((item) => {
            const existing = propertyMap.get(item.propertyId) || {
                propertyName: item.propertyName,
                amount: 0,
                count: 0,
            };
            existing.amount += item.due_amount || 0;
            existing.count += 1;
            propertyMap.set(item.propertyId, existing);
        });

        return Array.from(propertyMap.entries()).map(([propertyId, data]) => ({
            id: propertyId,
            propertyName: data.propertyName,
            amount: data.amount,
            paymentCount: data.count,
        }));
    }, [unitInstallments, properties]);

    const paymentUpdates = useMemo(() => {
        const paidItems = getAllPaidInstallments(unitInstallments, properties, 10);

        return paidItems.map((item) => {
            const status = mapInstallmentStatusToPaymentStatus(item.category, item.status);
            let finalStatus: 'in-process' | 'confirmed' = 'in-process';
            if (!(item.paid_amount && item.paid_amount < item.due_amount) && status === 'paid') {
                finalStatus = 'confirmed';
            }

            const paymentDate = item.due_date
                ? new Date(item.due_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
                : '';

            return {
                id: item.id,
                propertyName: item.propertyName,
                paymentTitle: item.type || item.payment_name,
                amount: item.paid_amount || item.amount || 0,
                status: finalStatus,
                paymentDate,
            };
        });
    }, [unitInstallments, properties]);

    const totalProperties = useMemo(() => properties.length, [properties]);

    const totalPurchasePrice = useMemo(() => {
        return properties.reduce((sum, property) => sum + (property.totalPrice || 0), 0);
    }, [properties]);

    return {
        overduePaymentIdsMap,
        overduePaymentCards,
        upcomingDuePaymentIdsMap,
        upcomingDuePaymentCards,
        paymentUpdates,
        totalProperties,
        totalPurchasePrice,
    };
}

function ReservationSummarySheet(props: Readonly<{
    visible: boolean;
    onClose: () => void;
    onDismiss: () => void;
    colors: ReturnType<typeof useUIColors>;
    t: ReturnType<typeof useTranslation>['t'];
    reservedUnitTotalPrice?: number;
    reservedUnitSanctionedLoan?: number;
    reservedUnitRequestedOnDisplay: string;
    reservationStatusColor: string;
    reservationStatusLabel: string;
    reservationCtas: ReturnType<typeof getReservationCtas>;
    onCancelReservationPress: () => void;
    onPayNowPress: () => void;
}>) {
    // Same rule as the unit-details Pay Now: only with a loan and a unit price above it.
    const outstandingBalanceAed = Math.max(
        0,
        (props.reservedUnitTotalPrice ?? 0) - (props.reservedUnitSanctionedLoan ?? 0),
    );
    const showPayNow = (props.reservedUnitSanctionedLoan ?? 0) > 0 && outstandingBalanceAed > 0;
    const showActionRow = props.reservationCtas.canCancelReservation || showPayNow;
    return (
        <BottomSheet
            visible={props.visible}
            onClose={props.onClose}
            dragHandleWidth={60}
            onDismiss={props.onDismiss}
        >
            <View
                style={[
                    styles.reservationSummaryContent,
                    {
                        borderColor: props.colors.outlineDisabled,
                        backgroundColor: props.colors.backgroundLevel2,
                    },
                ]}
            >
                <Typography
                    variant="headline"
                    color="primary"
                    style={styles.reservationSummaryTitle}
                >
                    {props.t('reserveUnit.reservationSummaryTitle')}
                </Typography>
                <DashedDivider color={props.colors.outlineDisabled} />

                {Boolean(props.reservedUnitTotalPrice) && (
                    <View style={styles.summaryRow}>
                        <Typography variant="body" color="primary">
                            {props.t('reserveUnit.totalUnitPrice')}
                        </Typography>
                        <View style={styles.summaryAmountRow}>
                            <DirhamIcon size={16} color={props.colors.fontPrimary} />
                            <Typography variant="body" color="primary">
                                {props.reservedUnitTotalPrice!.toLocaleString()}
                            </Typography>
                        </View>
                    </View>
                )}

                {Boolean(props.reservedUnitSanctionedLoan) && (
                    <View style={styles.summaryRow}>
                        <Typography variant="body" color="primary">
                            {props.t('reserveUnit.iskanLoan')}
                        </Typography>
                        <View style={styles.summaryAmountRow}>
                            <DirhamIcon size={16} color={props.colors.fontPrimary} />
                            <Typography variant="body" color="primary">
                                {props.reservedUnitSanctionedLoan!.toLocaleString()}
                            </Typography>
                        </View>
                    </View>
                )}

                {showPayNow && (
                    <View style={styles.summaryRow}>
                        <Typography variant="body" color="primary">
                            {props.t('propertyDetails.outstandingBalance')}
                        </Typography>
                        <View style={styles.summaryAmountRow}>
                            <DirhamIcon size={16} color={props.colors.remainingBalanceText} />
                            <Typography variant="body" color={props.colors.remainingBalanceText}>
                                {outstandingBalanceAed.toLocaleString()}
                            </Typography>
                        </View>
                    </View>
                )}

                {Boolean(props.reservedUnitRequestedOnDisplay) && (
                    <View style={styles.summaryRow}>
                        <Typography variant="body" color="primary">
                            {props.t('reserveUnit.requestedOn')}
                        </Typography>
                        <Typography variant="body" color="primary">
                            {props.reservedUnitRequestedOnDisplay}
                        </Typography>
                    </View>
                )}

                <View style={styles.summaryRow}>
                    <Typography variant="body" color="primary">
                        {props.t('reserveUnit.status')}
                    </Typography>
                    <Typography variant="body" color={props.reservationStatusColor}>
                        {props.reservationStatusLabel}
                    </Typography>
                </View>

                <DashedDivider color={props.colors.outlineDisabled} />

                {showActionRow && (
                    <View style={styles.appointmentActionRow}>
                        {props.reservationCtas.canCancelReservation && (
                            <TouchableOpacity
                                style={styles.appointmentRescheduleButton}
                                onPress={props.onCancelReservationPress}
                            >
                                <Typography variant="headline" style={styles.appointmentRescheduleText}>
                                    {props.t('reserveUnit.cancelReservation')}
                                </Typography>
                            </TouchableOpacity>
                        )}
                        {showPayNow && (
                            <TouchableOpacity
                                style={[
                                    styles.sheetPayNowButton,
                                    { backgroundColor: props.colors.buttonDefault },
                                ]}
                                onPress={props.onPayNowPress}
                            >
                                <Typography variant="headline" style={styles.sheetPayNowText}>
                                    {props.t('propertyDetails.payNow')}
                                </Typography>
                            </TouchableOpacity>
                        )}
                    </View>
                )}
            </View>
        </BottomSheet>
    );
}

/**
 * Web fallback branch of HomeScreenMobileAdhaMobile — extracted (Sonar
 * S3776: the `if (Platform.OS === 'web')` block's own `||` chain and
 * `&&` conditional pushed the parent's cognitive complexity over budget).
 * Pure presentational pass-through; no behavior change from before.
 */
function HomeScreenMobileAdhaWebFallback(props: Readonly<{
    venturesQuery: ReturnType<typeof useVentures>;
    user: ReturnType<typeof useAppStore.getState>['auth']['user'];
    userName: string;
    totalProperties: number;
    totalPurchasePrice: number;
    overduePaymentCards: {
        id: string;
        propertyName: string;
        amount: number;
        paymentCount: number;
    }[];
    upcomingDuePaymentCards: {
        id: string;
        propertyName: string;
        amount: number;
        paymentCount: number;
    }[];
    paymentUpdates: {
        id: string;
        propertyName: string;
        paymentTitle: string;
        amount: number;
        status: 'in-process' | 'confirmed';
        paymentDate: string;
    }[];
    homeCoverImage: string | null;
    profileLoading: boolean;
    unitInstallmentsLoading: boolean;
    handlePaymentCardPress: (propertyId: string, category: 'overdue' | 'upcomingDue') => void;
    handlePaymentUpdatePress: (paymentId: string) => void;
    handleVenturePress: (id: string) => void;
    unitInstallments: UnitInstallmentsMap;
    properties: Property[];
    paymentDetailsVisible: boolean;
    selectedPaymentId: string | undefined;
    setPaymentDetailsVisible: (visible: boolean) => void;
}>) {
    const { HomeScreenWeb } = require('./HomeScreenWeb');
    const userEmail = resolveUserEmail(props.user);
    const ventures = (props.venturesQuery.data || []).map((v) => ({
        id: v.id,
        logoUrl: v.logoUrl || '',
    }));

    return (
        <View style={{ flex: 1 }}>
            <HomeScreenWeb
                userName={props.userName}
                userEmail={userEmail}
                totalProperties={props.totalProperties}
                totalPurchasePrice={props.totalPurchasePrice}
                overduePaymentCards={props.overduePaymentCards}
                upcomingDuePaymentCards={props.upcomingDuePaymentCards}
                paymentUpdates={props.paymentUpdates}
                homeCoverImage={props.homeCoverImage}
                ventures={ventures}
                profileLoading={props.profileLoading}
                unitInstallmentsLoading={props.unitInstallmentsLoading}
                onPaymentCardPress={props.handlePaymentCardPress}
                onPaymentUpdatePress={props.handlePaymentUpdatePress}
                onVenturePress={props.handleVenturePress}
                onViewAllMissed={() => {
                    const overdueItems = getAllOverdueInstallments(props.unitInstallments, props.properties);
                    const paymentIds = overdueItems.map((item) => item.id);
                    if (paymentIds.length > 0) {
                        router.push({
                            pathname: '/(auth)/property-payments',
                            params: {
                                tab: 'upcoming',
                                selectedPaymentIds: paymentIds.join(','),
                            },
                        } as any);
                    }
                }}
                onViewAllDueSoon={() => {
                    const upcomingItems = getAllUpcomingDueInstallments(props.unitInstallments, props.properties);
                    const paymentIds = upcomingItems.map((item) => item.id);
                    if (paymentIds.length > 0) {
                        router.push({
                            pathname: '/(auth)/property-payments',
                            params: {
                                tab: 'upcoming',
                                selectedPaymentIds: paymentIds.join(','),
                            },
                        } as any);
                    }
                }}
                onViewAllHistory={() => {
                    router.push('/(auth)/property-payments?tab=history' as any);
                }}
            />
            {/* Payment details dialog for web – route is not changed */}
            {props.paymentDetailsVisible && (
                <PaymentDetailsDialog
                    visible={props.paymentDetailsVisible}
                    paymentId={props.selectedPaymentId}
                    onClose={() => props.setPaymentDetailsVisible(false)}
                />
            )}
        </View>
    );
}

/** Initial profile-load skeleton — extracted verbatim (Sonar S3776) from the parent's own early return. */
function HomeScreenMobileAdhaLoadingSkeleton(props: Readonly<{
    colors: ReturnType<typeof useUIColors>;
    insets: ReturnType<typeof useSafeAreaInsets>;
    layout: PropertiesLayout;
}>) {
    const { colors, insets, layout } = props;
    return (
        <View style={[styles.container, { backgroundColor: colors.backgroundLevel2 }]}>
            <ScrollView
                style={styles.scrollView}
                contentContainerStyle={[
                    styles.scrollContent,
                    { paddingBottom: 100 + insets.bottom },
                ]}
                showsVerticalScrollIndicator={false}
                contentInsetAdjustmentBehavior="never"
            >
                {/* Header Skeleton - mirrors the current ProfileNotificationHeader (no hero image in current UI) */}
                <View style={[styles.headerContainer, { paddingTop: insets.top + 8, paddingHorizontal: 16, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }]}>
                    <Skeleton width={120} height={24} />
                    <View style={{ flexDirection: 'row', gap: 16 }}>
                        <Skeleton width={24} height={24} variant="circular" />
                        <Skeleton width={24} height={24} variant="circular" />
                    </View>
                </View>

                {/* Greeting + layout toggle Skeleton */}
                <View style={styles.newGreetingContainer}>
                    <Skeleton width={200} height={28} />
                    <Skeleton width={72} height={32} borderRadius={8} />
                </View>

                {/* Exclusive Listings Skeleton */}
                <View style={[styles.section, styles.listingsSection]}>
                    <Skeleton width={160} height={16} />
                    <Skeleton width="100%" height={110} borderRadius={12} />
                    <View style={styles.listingsList}>
                        {[1, 2].map((key) => (
                            <PropertyListingCardSkeleton key={key} layout={layout} />
                        ))}
                    </View>
                </View>

                {/* Payment Sections Skeletons */}
                <View style={styles.section}>
                    <View style={styles.sectionHeader}>
                        <Skeleton width={18} height={18} variant="circular" />
                        <Skeleton width={140} height={18} />
                    </View>
                    <View style={styles.horizontalScrollContainer}>
                        <ScrollView
                            horizontal
                            showsHorizontalScrollIndicator={false}
                            contentContainerStyle={styles.horizontalScrollContent}
                        >
                            {PAYMENT_CARD_SKELETON_KEYS.map((key) => (
                                <View key={key} style={{ marginRight: 12, width: 160 }}>
                                    <PaymentCardSkeleton />
                                </View>
                            ))}
                        </ScrollView>
                    </View>
                </View>

                <View style={styles.section}>
                    <View style={styles.sectionHeader}>
                        <Skeleton width={18} height={18} variant="circular" />
                        <Skeleton width={180} height={18} />
                    </View>
                    <View style={styles.horizontalScrollContainer}>
                        <ScrollView
                            horizontal
                            showsHorizontalScrollIndicator={false}
                            contentContainerStyle={styles.horizontalScrollContent}
                        >
                            {PAYMENT_CARD_SKELETON_KEYS.map((key) => (
                                <View key={key} style={{ marginRight: 12, width: 160 }}>
                                    <PaymentCardSkeleton />
                                </View>
                            ))}
                        </ScrollView>
                    </View>
                </View>
            </ScrollView>
        </View>
    );
}

/** Profile-fetch error UI — extracted verbatim (Sonar S3776) from the parent's own early return. */
function HomeScreenMobileAdhaProfileError(props: Readonly<{
    colors: ReturnType<typeof useUIColors>;
    t: ReturnType<typeof useTranslation>['t'];
    profileError: ReturnType<typeof useProfile>['error'];
    retry: ReturnType<typeof useProfile>['retry'];
}>) {
    const { colors, t, profileError, retry } = props;
    const retryLabel = t('common.retry') || 'Retry';
    const runSingleTap = useSingleTapAction();
    return (
        <View style={[styles.container, { backgroundColor: colors.backgroundLevel2 }]}>
            <ProfileNotificationHeader
                showLogo={true}
                onProfilePress={() => runSingleTap(() => router.push('/profile'))}
                onNotificationPress={() => runSingleTap(() => router.push('/notifications'))}
            />
            <View style={styles.errorContainer}>
                <CircleAlertIcon size={48} color={colors.fontErrorText} />
                <Typography variant="headline" color="errorText" style={styles.errorTitle}>
                    {t('common.error')}
                </Typography>
                <Typography variant="body" color="tertiary" style={styles.errorMessage}>
                    {profileError}
                </Typography>
                <Button
                    title={retryLabel}
                    variant="primary"
                    onPress={retry}
                    style={styles.retryButton}
                />
            </View>
        </View>
    );
}

/**
 * Floating "Book Appointment" button — only shown when there's no appointment
 * booked yet. Extracted (Sonar S3776) so its `&&`/RTL-side ternary don't add
 * to the parent's cognitive complexity; same visibility/positioning as before.
 */
function HomeScreenMobileAdhaBookAppointmentFab(props: Readonly<{
    hasAppointment: boolean;
    bookingEnabled: boolean;
    bottomInset: number;
    isRTL: boolean;
    title: string;
    onPress: () => void;
}>) {
    if (props.hasAppointment || !props.bookingEnabled) {
        return null;
    }
    return (
        <View
            style={[
                styles.floatingButtonContainer,
                { bottom: 24 + props.bottomInset },
                props.isRTL ? { left: 0 } : { right: 0 },
            ]}
        >
            <BookAppointmentButton title={props.title} onPress={props.onPress} />
        </View>
    );
}

export function HomeScreenMobileAdhaMobile() {
    const venturesQuery = useVentures();
    // Grid/list toggle removed per the new design — Exclusive Listings is
    // permanently grid now. `layout` is kept as a plain constant (rather than
    // ripping out every prop it feeds) to minimize churn in the skeleton/card
    // rendering below, which still branches on it.
    const layout: PropertiesLayout = 'grid';
    const colors = useUIColors();
    const { t, currentLanguage } = useTranslation();
    // Rapid repeat taps would otherwise push the same screen several times.
    // Called before any early return (skeleton / profile error) below.
    const runSingleTap = useSingleTapAction();
    const contentfulLocale = currentLanguage === 'ar' ? 'ar' : 'en';
    const { isRTL } = useI18nContext();
    const insets = useSafeAreaInsets();
    const params = useLocalSearchParams<{
        skipPasscode?: string;
    }>();
    const skipPasscodeCalledRef = useRef(false);
    const [paymentDetailsVisible, setPaymentDetailsVisible] = useState(false);
    const [selectedPaymentId, setSelectedPaymentId] = useState<string | undefined>(undefined);

    // Profile fetching hook
    const { isLoading: profileLoading, error: profileError, fetchProfile, retry } = useProfile();

    // Unit installments fetching hook
    const { fetchAllUnitInstallments } = useUnitInstallments();

    // Passcode hook for skip passcode
    const { skipPasscode } = usePasscode();

    // Get authentication state to prevent API calls when logged out
    const isAuthenticated = useAppStore((state) => state.auth.isAuthenticated);

    // Get loading states from store
    const unitInstallmentsLoading = useAppStore((state) => state.unitInstallmentsLoading);

    // Currently booked/upcoming appointment, sourced from the shared app store
    // (see PropertyDetailsScreen/BookAppointmentScreen for where this is set/cleared)
    const appointment = useAppStore((state) => state.appointment);

    // Reserved unit (Reserve Unit flow), shown as a second Home status card
    // alongside the upcoming appointment card — see ReserveUnitScreen for where this is set.
    const reservedUnit = useAppStore((state) => state.reservedUnit);
    // Seed source for the reserved unit's catalogue row — GET /v1/units never
    // returns the currently-booked unit (same as web's PropertiesListScreen
    // displayListings comment), so it has to be reconstructed from these.
    const pendingUnitReservationForListing = useAppStore((state) => state.pendingUnitReservation);
    const storeAvailableUnitsForListing = useAppStore((state) => state.availableUnits);
    const { cancelReservation } = useReservationActions(reservedUnit);
    const customerId = useAppStore((state) => state.auth.user?.customer_id);

    const adhaProjectName = useAppStore(selectAdhaProjectName);
    const adhaProjectId = useAppStore(selectAdhaProjectId);
    const adhaProjectLocation = useAppStore(selectAdhaProjectLocation);
    const [contentfulImages, setContentfulImages] = React.useState<
        ImageSourcePropType[]
    >([]);
    // Null until Contentful succeeds. Then the same unitImagesByUnitModel lookup
    // web listing uses for per-unit galleries.
    const [contentfulUnitImageLookup, setContentfulUnitImageLookup] = React.useState<
        Record<string, PropertyImage[]> | null
    >(null);
    const [contentfulName, setContentfulName] = React.useState<string | undefined>();

    // State for home cover image
    const [homeCoverImage, setHomeCoverImage] = React.useState<string | null>(null);

    // State for property images (exclusive listings section), mapped by project name
    // Superseded by the mock catalogue's own per-unit images (see availableUnits below) —
    // kept in place instead of deleted.
    // const [propertyImagesMap, setPropertyImagesMap] = useState<Record<string, ImageSourcePropType[]>>({});

    // Exclusive listings: max price filter (in millions, AED). Lands at 1.7M, same as web.
    // Dragging to 3M+ sends the no-limit sentinel.
    const profileLoanAmount = useAppStore((state) => state.auth.user?.loan_Amount);
    const storedLoanAmountForSlider = useAppStore(
        (state) => state.adhaLoanDetails?.sanctionedLoanAmount,
    );
    // Fixed loan amount (AED, unclamped) for the slider's header label only —
    // independent of the thumb position / filter default below.
    const iskanLoanAed = useMemo(
        () => resolveIskanLoanAmountAed(profileLoanAmount, storedLoanAmountForSlider),
        [profileLoanAmount, storedLoanAmountForSlider],
    );
    const [maxPrice, setMaxPrice] = useState(() =>
        defaultSliderMillionsForBedrooms(
            useUnitListingFilterStore.getState().appliedBedrooms,
            PRICE_SLIDER_MAX_M,
        ),
    );
    const handleMaxPriceChange = useCallback((v: number) => {
        const clamped = Math.min(Math.max(v, PRICE_SLIDER_MIN_M), PRICE_SLIDER_MAX_M);
        setMaxPrice(isFullPriceRange(clamped) ? PRICE_SLIDER_MAX_M : clamped);
    }, []);
    const [priceFilterSheetVisible, setPriceFilterSheetVisible] = useState(false);

    // States for upcoming appointment & reschedule/cancel flows
    const [actionsPopupVisible, setActionsPopupVisible] = useState(false);
    const [cancelDialogVisible, setCancelDialogVisible] = useState(false);
    const [cancelSuccessVisible, setCancelSuccessVisible] = useState(false);
    const [isCancelingAppointment, setIsCancelingAppointment] = useState(false);

    // States for the reserved-unit card's status-badge flow — opens
    // Reservation Summary, mirrors PropertyDetailsScreenAdhaMobile's sheet.
    const [reservationSummaryVisible, setReservationSummaryVisible] = useState(false);
    const [cancelReservationVisible, setCancelReservationVisible] = useState(false);
    const [reservationCanceledVisible, setReservationCanceledVisible] = useState(false);
    const [isCancelingReservation, setIsCancelingReservation] = useState(false);
    const pendingReservationSheetRef = useRef<(() => void) | null>(null);
    const [toastVisible, setToastVisible] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    const handleCloseActionsPopup = () => {
        setActionsPopupVisible(false);
    };

    const handleReschedulePress = () => {
        const unitId = appointment?.propertyId?.trim();
        if (unitId) {
            trackCatalogEvent({
                screen: ALMETLAA_SCREEN.UNIT,
                actionName: ALMETLAA_ACTION.UNIT.bookAppointmentClick(unitId),
                eventType: 'action',
            });
        }
        setActionsPopupVisible(false);
        runSingleTap(() =>
            router.push({
                pathname: '/(auth)/book-appointment',
                params: {
                    propertyId: appointment?.propertyId ?? '',
                    eventId: appointment?.eventId ?? '',
                    mode: 'reschedule',
                },
            } as any),
        );
    };

    const handleCancelRequestPress = () => {
        setActionsPopupVisible(false);
        // Open the Cancel Confirmation Dialog
        setCancelDialogVisible(true);
    };

    // Same as web's `useBookAppointmentFlow.confirmCancel` — resolve the event_id
    // (from the store, falling back to GET /upcoming) and actually cancel on the
    // backend before clearing local state, instead of just clearing locally.
    // Strictly uses `event_id` — never falls back to appointment_id/id, since the
    // backend treats them as different fields (see web's useBookAppointmentFlow).
    const handleConfirmCancel = async () => {
        await confirmCancelAppointmentOnServer({
            appointment,
            primaryUnitId,
            setCancelDialogVisible,
            setCancelSuccessVisible,
            setIsCanceling: setIsCancelingAppointment,
        });
    };

    // Auto skip passcode on web after login
    // Only call if we don't already have a passcodeToken (skip was already called)
    const webSkipPasscodeCalledRef = useRef(false);
    useEffect(() => {
        autoSkipPasscodeOnWebIfNeeded({
            isAuthenticated,
            calledRef: webSkipPasscodeCalledRef,
            skipPasscode,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated]);

    // Fetch home cover image from Contentful (again after an app-wide Contentful refresh)
    const contentfulRevision = useContentfulRevision();
    React.useEffect(() => {
        fetchHomeCoverImage('home-cover')
            .then((url) => {
                if (url) {
                    setHomeCoverImage(url);
                }
            })
            .catch((err) => {
                logger.error('HomeScreen', 'Failed to fetch home cover image', { error: err });
            });
    }, [contentfulRevision]);

    // Handle skip passcode on login
    useEffect(() => {
        if (params.skipPasscode !== 'true' || skipPasscodeCalledRef.current) {
            return;
        }
        void handleSkipPasscodeFlag({
            calledRef: skipPasscodeCalledRef,
            skipPasscode,
            isAuthenticated,
            fetchProfile,
            fetchAllUnitInstallments,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.skipPasscode, isAuthenticated]);

    // Fetch profile on mount - only when authenticated
    // CRITICAL: fetchProfile and fetchAllUnitInstallments are stable (don't change on re-renders)
    // Only depend on isAuthenticated to prevent infinite loops
    // Skip if skipPasscode is being handled (it will fetch after skip completes)
    useEffect(() => {
        return fetchProfileOnMountIfNeeded({
            isAuthenticated,
            skipPasscodeParam: params.skipPasscode,
            skipPasscodeCalledRef,
            fetchProfile,
            fetchAllUnitInstallments,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, params.skipPasscode]); // Only depend on isAuthenticated and skipPasscode - functions are stable

    const handlePaymentCardPress = (propertyId: string, category: 'overdue' | 'upcomingDue') => {
        runSingleTap(() =>
            router.push({
                pathname: '/(auth)/property-payments',
                params: resolvePaymentCardNavParams(category, propertyId, overduePaymentIdsMap, upcomingDuePaymentIdsMap),
            } as any),
        );
    };

    const handleVenturePress = (id: string) => {
        runSingleTap(() => router.push({ pathname: '/venture/[id]', params: { id } } as any));
    };

    // Get user name for greeting
    const user = useAppStore((state) => state.auth.user);

    // Extract first name using reusable utility function
    const userName = getUserFirstName(user);

    // Contentful time-of-day prefix (home.goodMorning / Afternoon / Evening),
    // same helpers as ADHA web listing. State + interval so it tracks local
    // time and re-resolves when Contentful translations load.
    const [greetingPrefix, setGreetingPrefix] = useState(() => resolveGreetingPrefix(t));
    useEffect(() => {
        setGreetingPrefix(resolveGreetingPrefix(t));
        const intervalId = setInterval(() => {
            setGreetingPrefix(resolveGreetingPrefix(t));
        }, 60000);
        return () => clearInterval(intervalId);
    }, [t]);
    const greeting = formatNamedGreeting(greetingPrefix, userName);

    // Get real data from store
    const properties = useAppStore((state) => state.properties);
    const unitInstallments = useAppStore((state) => state.unitInstallments);

    // Fetch property images for exclusive listings section when properties change
    // Superseded by the mock catalogue's own per-unit images — kept in place instead of deleted.
    // useEffect(() => {
    //     const projectNames = Array.from(
    //         new Set(properties.map((p) => p.projectName).filter(Boolean)),
    //     ) as string[];
    //
    //     if (projectNames.length === 0) return;
    //
    //     fetchPropertyImagesForProjects(projectNames)
    //         .then((imagesMap) => setPropertyImagesMap(imagesMap))
    //         .catch((err) => {
    //             logger.error('HomeScreen', 'Failed to fetch property images', { error: err });
    //         });
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [properties]);

    // Exclusive listings: user's ADHA-eligible units (falls back to adhaLoanDetails.projectId
    // from the store, same as web's PropertiesListScreen), filtered by the price slider.
    // Opens at 1.7M (max_price 1_700_000). 3M+ sends the no-limit sentinel.
    const maxPriceFilterAed = resolveMaxPriceFilterAed(maxPrice);
    const {
        data: availableUnitsData,
        isLoading: availableUnitsLoading,
        isError: availableUnitsError,
        isFetchingFirstPage,
        isFetchingNextPage,
        refetch: refetchAvailableUnits,
    } = useAvailableUnits(
        undefined,
        maxPriceFilterAed,
        true,
    );
    useCatalogDynatraceView(
        ALMETLAA_SCREEN.CATALOGUE,
        ALMETLAA_ACTION.CATALOGUE.HOME_VIEW,
        !isFetchingFirstPage && !availableUnitsLoading,
    );
    const availableUnitsDataUnits = availableUnitsData?.units;
    const availableUnits = useMemo(() => resolveDisplayAvailableUnits(
        availableUnitsDataUnits || [],
        reservedUnit?.propertyId,
        pendingUnitReservationForListing,
        storeAvailableUnitsForListing,
        maxPriceFilterAed,
    ), [
        availableUnitsDataUnits,
        reservedUnit?.propertyId,
        pendingUnitReservationForListing,
        storeAvailableUnitsForListing,
        maxPriceFilterAed,
    ]);
    const appliedBedrooms = useUnitListingFilterStore((state) => state.appliedBedrooms);
    const setAppliedBedrooms = useUnitListingFilterStore((state) => state.setAppliedBedrooms);
    const catalogueUnits = useMemo(
        () => filterUnitsByBedrooms(availableUnits, appliedBedrooms),
        [availableUnits, appliedBedrooms],
    );
    const openCatalogueUnit = useCallback((unitId: string) => {
        const unit = availableUnits.find((item) => item.unit_id === unitId);
        const cardImages = unit && contentfulUnitImageLookup
            ? resolveListingCardImages(
                contentfulUnitImageLookup,
                readListingUnitModel(unit),
                unit.unit_id,
            ).slice(0, 1)
            : [];
        runSingleTap(() => {
            rememberAdhaHeroImages(unitId, cardImages);
            router.push({
                pathname: '/(auth)/property-details',
                params: { propertyId: unitId },
            } as any);
        });
    }, [availableUnits, contentfulUnitImageLookup, runSingleTap]);

    /**
     * TEMP: upcoming appointment API is scoped to catalogue unit index 0 until
     * per-listing unit mapping is wired (same interim approach as web listing).
     */
    const primaryUnitId = availableUnits[0]?.unit_id ?? '';

    const { bookingQuery: adhaBookingQuery, profileQuery: adhaProfileQuery } = useHomeScreenBookingSync(
        customerId,
        adhaProjectName ?? '',
        adhaProjectLocation,
        t,
        contentfulName,
    );

    const [isReservationRefreshing, setIsReservationRefreshing] = useState(false);
    // Full refresh — every screen section, not just the reserved-unit card:
    // booking + profile (which the reserved-unit card and its "Status" derive
    // from), the upcoming-appointment card, the available-units catalogue
    // (unit prices/listing status the reserved card also reads via
    // resolveReservationDisplayState's availableUnits.find), and ventures.
    // Everything this screen shows from Contentful, fetched fresh (cache bypassed).
    // Used by pull-to-refresh and when the user comes back to Home.
    const refreshHomeContentful = useCallback(async () => {
        await Promise.all([
            venturesQuery.refetch(),
            fetchAdhaHomeContentfulAssets({
                projectId: adhaProjectId,
                projectName: adhaProjectName,
                locale: contentfulLocale,
                setContentfulImages,
                setContentfulUnitImageLookup,
                setContentfulName,
                forceRefresh: true,
            }),
            refreshContentfulTranslations(contentfulLocale),
            fetchHomeCoverImage('home-cover')
                .then((url) => {
                    if (url) {
                        setHomeCoverImage(url);
                    }
                })
                .catch((error) => {
                    logger.warn('HomeScreenMobileAdhaMobile:cover refresh failed', error);
                }),
        ]);
    }, [venturesQuery, adhaProjectId, adhaProjectName, contentfulLocale]);

    const handlePullToRefresh = useCallback(async () => {
        setIsReservationRefreshing(true);
        try {
            await Promise.all([
                adhaBookingQuery.refetch(),
                adhaProfileQuery.refetch(),
                refreshUpcomingAppointment(primaryUnitId, t, isRTL),
                refetchAvailableUnits(),
                refreshHomeContentful(),
            ]);
        } catch (error) {
            logger.warn('HomeScreenMobileAdhaMobile:pull-to-refresh failed', error);
        } finally {
            setIsReservationRefreshing(false);
        }
    }, [
        adhaBookingQuery,
        adhaProfileQuery,
        primaryUnitId,
        t,
        isRTL,
        refetchAvailableUnits,
        refreshHomeContentful,
    ]);

    // Coming back to Home (e.g. from unit details) refreshes the Contentful
    // content too. The first focus is skipped: the mount effects already load it.
    // Runs after the transition so it does not compete with navigation, like
    // the catalogue refresh in useAvailableUnits.
    const refreshHomeContentfulRef = useRef(refreshHomeContentful);
    refreshHomeContentfulRef.current = refreshHomeContentful;
    const didFocusHomeRef = useRef(false);
    useFocusEffect(
        useCallback(() => {
            if (!didFocusHomeRef.current) {
                didFocusHomeRef.current = true;
                return undefined;
            }
            let cancelled = false;
            const timeoutId = setTimeout(() => {
                if (cancelled) {
                    return;
                }
                refreshHomeContentfulRef.current().catch((error) => {
                    logger.warn('HomeScreenMobileAdhaMobile:focus Contentful refresh failed', error);
                });
            }, 0);
            return () => {
                cancelled = true;
                clearTimeout(timeoutId);
            };
        }, []),
    );

    // Don't show the reserved-unit card at all until this session's booking
    // fetch has settled at least once — `reservedUnit` is persisted from the
    // last session, so rendering it immediately (before the fresh status
    // arrives) could show a stale or locally-guessed status ("Pending")
    // that then visibly flips once the real data lands a moment later.
    const reservedUnitForDisplay =
        !customerId || adhaBookingQuery.isFetched ? reservedUnit : null;

    // Reservation CTAs for the reserved-unit card's 3-dot menu — same shared
    // resolver used on PropertyDetailsScreenAdhaMobile (src/core/reservation).
    const {
        reservationCtas,
        reservationListingMessage,
        reservationStatusLabel,
        reservationStatusColor,
        reservedUnitTotalPrice,
    } = resolveReservationDisplayState({
        reservedUnit: reservedUnitForDisplay,
        appointment,
        availableUnits,
        colors,
        t,
    });
    // Reservation Summary sheet's "Requested on" always comes from profile-details
    // API's `booking_date` field — no fallback to other fields or to "now()",
    // since a wrong date is worse than an empty one.
    const reservedUnitRequestedOnDisplay = useMemo(() => {
        return formatBookingRequestedOn(adhaProfileQuery.data?.booking_date || '');
    }, [adhaProfileQuery.data?.booking_date]);

    // Reserved-unit card's status badge is the single entry point now (3-dot
    // menu removed) — tapping it opens Reservation Summary directly.
    const handleOpenReservationSummary = () => {
        const unitId = reservedUnitForDisplay?.propertyId;
        trackAdhaReservationCardClick(unitId);
        trackReservationSummaryOpen(unitId);
        setReservationSummaryVisible(true);
    };

    // Pay Now from the reservation sheet — same destination and params as the
    // unit-details Pay Now. On iOS the sheet must finish closing first.
    const handleReservationPayNowPress = () => {
        if (!reservedUnit?.propertyId) {
            return;
        }
        trackCatalogEvent({
            screen: ALMETLAA_SCREEN.TOP_UP,
            actionName: ALMETLAA_ACTION.TOP_UP.COMPLETE_PAYMENT_SHEET_OPEN,
            eventType: 'action',
            properties: { unitId: reservedUnit.propertyId },
        });
        const navigate = () =>
            runSingleTap(() =>
                router.push({
                    pathname: '/(auth)/payment-method-select',
                    params: {
                        propertyId: reservedUnit.propertyId,
                        unitLabel: reservedUnit.unitLabel,
                        totalPrice: Number(reservedUnitTotalPrice ?? 0).toString(),
                        iskanLoan: iskanLoanAed.toString(),
                        returnTo: 'home',
                    },
                } as any),
            );
        if (Platform.OS === 'ios') {
            pendingReservationSheetRef.current = navigate;
            setReservationSummaryVisible(false);
            return;
        }
        setReservationSummaryVisible(false);
        navigate();
    };

    const handleCancelReservationPress = () => {
        trackCatalogEvent({
            screen: ALMETLAA_SCREEN.RESERVE,
            actionName: ALMETLAA_ACTION.RESERVE.CANCEL_DIALOG_OPEN,
            eventType: 'action',
        });
        queueReservationCancelConfirm({
            isIos: Platform.OS === 'ios',
            pendingSheetRef: pendingReservationSheetRef,
            hideSummary: () => setReservationSummaryVisible(false),
            showConfirm: () => setCancelReservationVisible(true),
        });
    };

    const handleConfirmCancelReservation = async () => {
        trackCatalogEvent({
            screen: ALMETLAA_SCREEN.RESERVE,
            actionName: ALMETLAA_ACTION.RESERVE.CANCEL_CONFIRM,
            eventType: 'action',
        });
        setCancelReservationVisible(false);
        setIsCancelingReservation(true);
        try {
            const { success, message } = await cancelReservation();
            logger.info('HomeScreenMobileAdhaMobile:cancelReservation result', {
                success,
                message,
            });
            if (success) {
                setReservationCanceledVisible(true);
                return;
            }
            setToastMessage(message);
            setToastVisible(true);
        } finally {
            setIsCancelingReservation(false);
        }
    };

    useHomeScreenUpcomingSync(isAuthenticated, primaryUnitId, t, isRTL);

    React.useEffect(() => {
        void fetchAdhaHomeContentfulAssets({
            projectId: adhaProjectId,
            projectName: adhaProjectName,
            locale: contentfulLocale,
            setContentfulImages,
            setContentfulUnitImageLookup,
            setContentfulName,
        });
    }, [adhaProjectId, adhaProjectName, contentfulLocale, contentfulRevision]);

    const {
        overduePaymentIdsMap,
        overduePaymentCards,
        upcomingDuePaymentIdsMap,
        upcomingDuePaymentCards,
        paymentUpdates,
        totalProperties,
        totalPurchasePrice,
    } = useHomeScreenPaymentData(unitInstallments, properties);

    const handlePaymentUpdatePress = (paymentId: string) => {
        if (Platform.OS === 'web') {
            // On web, open dialog instead of changing route
            setSelectedPaymentId(paymentId);
            setPaymentDetailsVisible(true);
            return;
        }
        runSingleTap(() => router.push(`/(auth)/payment-details?id=${paymentId}` as any));
    };

    // Show skeleton loading for initial profile load
    // After profile loads, show skeleton states for units progressively
    if (profileLoading) {
        return <HomeScreenMobileAdhaLoadingSkeleton colors={colors} insets={insets} layout={layout} />;
    }

    // Show error UI if profile fetch failed
    if (profileError) {
        return <HomeScreenMobileAdhaProfileError colors={colors} t={t} profileError={profileError} retry={retry} />;
    }

    // Conditionally render web vs mobile version
    if (Platform.OS === 'web') {
        return (
            <HomeScreenMobileAdhaWebFallback
                venturesQuery={venturesQuery}
                user={user}
                userName={userName}
                totalProperties={totalProperties}
                totalPurchasePrice={totalPurchasePrice}
                overduePaymentCards={overduePaymentCards}
                upcomingDuePaymentCards={upcomingDuePaymentCards}
                paymentUpdates={paymentUpdates}
                homeCoverImage={homeCoverImage}
                profileLoading={profileLoading}
                unitInstallmentsLoading={unitInstallmentsLoading}
                handlePaymentCardPress={handlePaymentCardPress}
                handlePaymentUpdatePress={handlePaymentUpdatePress}
                handleVenturePress={handleVenturePress}
                unitInstallments={unitInstallments}
                properties={properties}
                paymentDetailsVisible={paymentDetailsVisible}
                selectedPaymentId={selectedPaymentId}
                setPaymentDetailsVisible={setPaymentDetailsVisible}
            />
        );
    }

    return (
        <View style={[styles.container, { backgroundColor: colors.backgroundLevel2 }]}>
            {/* Sticky block - header + greeting stay pinned while only the
                listings below scroll, instead of scrolling away with them. */}
            <View style={[styles.stickyHeader, { backgroundColor: colors.backgroundLevel2 }]}>
                {/* Header - reused from PropertiesScreen (My Properties) header instead of the old image/greeting header below */}
                <View style={styles.headerContainer}>
                    <ProfileNotificationHeader
                        showLogo={true}
                        onProfilePress={() => runSingleTap(() => router.push('/profile'))}
                        onNotificationPress={() => runSingleTap(() => router.push('/notifications'))}
                        // Now rendered outside the ScrollView (sticky block), so it no
                        // longer benefits from contentInsetAdjustmentBehavior="automatic"
                        // — both platforms need the safe-area inset applied explicitly.
                        style={{ paddingTop: insets.top + 8 }}
                    />
                </View>

                {/* Greeting Text - shown below the new header, same as before */}
                <View style={styles.newGreetingContainer}>
                    <Typography variant="title2" color="primary" numberOfLines={2} style={styles.newGreetingText}>
                        {greeting}
                    </Typography>
                    {/* Grid/list toggle replaced with this icon per the new design —
                        opens the price-range filter (moved off the home screen into
                        its own bottom sheet). Centered against the greeting text via
                        newGreetingContainer's own alignItems: 'center'. */}
                    <TouchableOpacity
                        style={styles.slidersButton}
                        accessibilityRole="button"
                        onPress={() => setPriceFilterSheetVisible(true)}
                    >
                        <SlidersIcon size={24} color={colors.buttonDefault} />
                    </TouchableOpacity>
                </View>
            </View>

            <ExclusiveListingsSection
                upcomingAppointment={appointment}
                reservedUnit={reservedUnitForDisplay}
                reservationListingMessage={reservationListingMessage}
                onUpcomingMenuPress={() => {
                    trackAdhaAppointmentCardClick(appointment?.propertyId);
                    setActionsPopupVisible(true);
                }}
                onReservedCardPress={handleOpenReservationSummary}
                carouselPaused={actionsPopupVisible || reservationSummaryVisible}
                t={t}
                availableUnitsLoading={availableUnitsLoading}
                isFetchingFirstPage={isFetchingFirstPage}
                isFetchingNextPage={isFetchingNextPage}
                availableUnits={catalogueUnits}
                adhaProjectName={adhaProjectName ?? ''}
                adhaProjectLocation={adhaProjectLocation}
                contentfulImages={contentfulImages}
                contentfulUnitImageLookup={contentfulUnitImageLookup}
                contentfulName={contentfulName}
                projectId={adhaProjectId ?? ''}
                emptyKind={resolveListingEmptyKind(availableUnitsError)}
                contentPaddingBottom={(appointment ? 24 : 100) + insets.bottom}
                refreshControl={
                    <RefreshControl
                        refreshing={isReservationRefreshing}
                        onRefresh={handlePullToRefresh}
                        tintColor={colors.bloomRed}
                        colors={[colors.bloomRed]}
                    />
                }
            />

            {/* Book Appointment Floating Button — only shown when there's no appointment
                booked yet; tapping it starts a fresh booking on BookAppointmentScreen. */}
            <HomeScreenMobileAdhaBookAppointmentFab
                hasAppointment={!!appointment}
                // Same rule as web's PropertiesListScreen Book FAB.
                bookingEnabled={
                    isAdhaProfileActionEnabled(adhaProfileQuery.data?.isAppointmentBookingEnabled) &&
                    !isAppointmentBookingWindowClosed(
                        adhaProfileQuery.data?.appointmentBookingStartDate,
                        adhaProfileQuery.data?.appointmentBookingEndDate,
                    )
                }
                bottomInset={insets.bottom}
                isRTL={isRTL}
                title={t('bookAppointment.bookAppointment')}
                onPress={() => {
                    runSingleTap(() => {
                        if (primaryUnitId) {
                            trackCatalogEvent({
                                screen: ALMETLAA_SCREEN.UNIT,
                                actionName: ALMETLAA_ACTION.UNIT.bookAppointmentClick(primaryUnitId),
                                eventType: 'action',
                            });
                        }
                        router.push({
                            pathname: '/(auth)/book-appointment',
                            params: {
                                propertyId: primaryUnitId,
                                mode: 'book',
                            },
                        } as any);
                    });
                }}
            />

            {/* Dialogs / Modals */}
            <PriceRangeFilterSheet
                visible={priceFilterSheetVisible}
                onClose={() => setPriceFilterSheetVisible(false)}
                maxPrice={maxPrice}
                setMaxPrice={handleMaxPriceChange}
                loanAmountAed={iskanLoanAed}
                units={availableUnits}
                projectName={adhaProjectName ?? ''}
                appliedBedrooms={appliedBedrooms}
                onApplyBedrooms={setAppliedBedrooms}
                onUnitPress={openCatalogueUnit}
            />

            <AppointmentActionsPopup
                visible={actionsPopupVisible}
                onClose={handleCloseActionsPopup}
                onReschedule={handleReschedulePress}
                onCancelRequest={handleCancelRequestPress}
            />

            <CancelAppointmentDialog
                visible={cancelDialogVisible}
                title={t('propertyDetails.cancelAppointment')}
                message={t('bookAppointment.cancelMessage')}
                noText={t('common.no')}
                yesText={t('common.yes')}
                onNo={() => setCancelDialogVisible(false)}
                onYes={handleConfirmCancel}
            />

            <AppointmentCanceledDialog
                visible={cancelSuccessVisible}
                onAutoClose={() => setCancelSuccessVisible(false)}
            />

            {/* View Reservation summary sheet */}
            <ReservationSummarySheet
                visible={reservationSummaryVisible}
                onClose={() => setReservationSummaryVisible(false)}
                onDismiss={() => {
                    const openConfirm = pendingReservationSheetRef.current;
                    pendingReservationSheetRef.current = null;
                    openConfirm?.();
                }}
                colors={colors}
                t={t}
                reservedUnitTotalPrice={reservedUnitTotalPrice}
                // Same loan as unit details' Iskan loan (profile loan_Amount first).
                reservedUnitSanctionedLoan={iskanLoanAed}
                reservedUnitRequestedOnDisplay={reservedUnitRequestedOnDisplay}
                reservationStatusColor={reservationStatusColor}
                reservationStatusLabel={reservationStatusLabel}
                reservationCtas={reservationCtas}
                onCancelReservationPress={handleCancelReservationPress}
                onPayNowPress={handleReservationPayNowPress}
            />

            <ReservationCanceledDialog
                visible={cancelReservationVisible || reservationCanceledVisible}
                phase={reservationCanceledVisible ? 'success' : 'confirm'}
                onNo={() => setCancelReservationVisible(false)}
                onYes={handleConfirmCancelReservation}
                onAutoClose={() => setReservationCanceledVisible(false)}
            />

            <Toast
                visible={toastVisible}
                message={toastMessage}
                onHide={() => setToastVisible(false)}
            />
            <FullScreenLoader visible={isCancelingAppointment || isCancelingReservation} />

            {/* Payment details dialog for web – route is not changed */}
            {paymentDetailsVisible && (
                <PaymentDetailsDialog
                    visible={paymentDetailsVisible}
                    paymentId={selectedPaymentId}
                    onClose={() => setPaymentDetailsVisible(false)}
                />
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        marginTop: 0,
        paddingTop: 0,
        // backgroundColor applied in component for theme support
    },
    scrollView: {
        flex: 1,
        marginTop: 0,
        paddingTop: 0,
    },
    listingScrollHit: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        right: 0,
        width: LISTING_THUMB_HIT_WIDTH,
        zIndex: 2,
    },
    listingScrollThumb: {
        position: 'absolute',
        right: LISTING_THUMB_INSET,
        width: LISTING_THUMB_WIDTH,
        borderRadius: LISTING_THUMB_WIDTH / 2,
    },
    scrollContent: {
        paddingBottom: 100,
        paddingTop: 0,
    },
    stickyHeader: {
        zIndex: 1,
    },
    headerContainer: {
        paddingHorizontal: 0,
    },
    newGreetingContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingTop: 12,
        paddingBottom: 0,
        gap: 16,
    },
    newGreetingText: {
        flex: 1,
    },
    slidersButton: {
        width: 32,
        height: 32,
        alignItems: 'center',
        justifyContent: 'center',
    },
    headerBackground: {
        width: '100%',
        height: 240, // Figma: 240px height
        position: 'relative',
        overflow: 'hidden', // Ensure content doesn't overflow
    },
    headerImage: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
    },
    headerOverlayGradient: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        opacity: 0.5, // Figma: opacity 50%
    },
    headerTopBar: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 30,
    },
    greetingContainer: {
        position: 'absolute',
        left: 0,
        right: 0,
        top: 120, // Position higher up in the header to avoid overlap with stats card
        width: '100%',
        paddingHorizontal: 16,
        zIndex: 20,
        alignItems: 'center',
        justifyContent: 'center',
    },
    greeting: {
        textAlign: 'center',
        fontSize: 24,
    },
    // Stats Card - 50% overlapping the header image (positioned at 200px from top, header ends at 240px)
    // Assuming card height ~80px, 50% = 40px overlap, so card starts at 240 - 40 = 200px
    statsCardWrapper: {
        marginHorizontal: 16,
        marginTop: -40, // Negative margin to overlap 50% of card height on the image
        marginBottom: 20, // Figma: gap: 20px
        zIndex: 10,
    },
    statsCard: {
        flexDirection: 'row',
        paddingVertical: 16,
        paddingHorizontal: 4,
        gap: 12,
        // Enhanced shadow for better depth
        // shadowColor applied in component for theme support
        shadowOffset: {
            width: 0,
            height: 4,
        },
        shadowOpacity: 0.15,
        shadowRadius: 8,
        elevation: 8,
    },
    listingsSection: {
        gap: 12,
    },
    listingsList: {
        gap: 16,
    },
    listingRow: {
        marginBottom: 16,
        paddingHorizontal: 16,
    },
    listingsEmptyState: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 24,
        gap: 8,
    },
    listingsEmptyTitle: {
        textAlign: 'center',
        marginTop: 8,
    },
    listingsEmptySubtitle: {
        textAlign: 'center',
    },
    statSection: {
        flex: 1,
        alignItems: 'center',
        paddingVertical: 16,
        paddingHorizontal: 12,
    },
    statLabel: {
        marginBottom: 4,
        textAlign: 'center',
    },
    statValue: {
        textAlign: 'center',
    },
    priceContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    priceValue: {
        textAlign: 'center',
    },
    section: {
        marginBottom: 20,
        paddingHorizontal: 16,
        marginTop: 12,
    },
    sectionHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        height: 24,
        marginBottom: 8,
    },
    sectionTitle: {
        flex: 1,
    },
    horizontalScrollContent: {
        gap: 12,
        paddingLeft: 16, // Left padding to prevent cards from touching edge
        paddingRight: 16, // Right padding to show there are more items
    },
    horizontalScrollContainer: {
        marginHorizontal: -16, // Negative margin to extend to screen edges
    },
    paymentCard: {
        // backgroundColor applied in component for theme support
        borderRadius: 12,
        width: 141,
        height: 84,
        position: 'relative', // For absolute positioning
    },
    paymentCardContent: {
        position: 'absolute',
        top: 12, // Exact Figma positioning
        left: 12, // Exact Figma positioning
        right: 12, // Add right padding for arrow
        height: 60, // Exact Figma measurement
        gap: 8,
    },
    propertyName: {
        width: '100%',
    },
    amountRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
    },
    amountContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        flexShrink: 1, // Allow container to shrink if needed
        minWidth: 0, // Allow flex children to shrink below their content size
    },
    amount: {
        flexShrink: 1, // Allow text to shrink for large numbers
        minWidth: 0, // Allow text to shrink below content size
        paddingTop: 2,
    },
    paymentCount: {
        width: '100%',
    },
    paymentUpdateCard: {
        width: 256,
        height: 77, // Exact Figma measurement
        padding: 0,
        position: 'relative', // For absolute positioning
        overflow: 'hidden',
    },
    updatePropertyName: {
        position: 'absolute',
        top: 12, // Exact Figma positioning
        left: 12, // Exact Figma positioning
        right: 12,
    },
    updateContent: {
        position: 'absolute',
        top: 30, // Exact Figma positioning
        left: 12, // Exact Figma positioning
        right: 12,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    updateTitle: {
        flex: 1,
        marginRight: 8,
    },
    updateAmountContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 2,
        flexShrink: 0,
    },
    updateAmount: {
        flexShrink: 0,
        paddingTop: 2,
    },
    statusContainer: {
        position: 'absolute',
        bottom: 12, // Position from bottom instead of top
        left: 12,
        right: 12,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 4,
    },
    emptyState: {
        paddingVertical: 16,
        paddingHorizontal: 12,
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 200,
    },
    venturesTitle: {
        marginBottom: 12,
    },
    ventureCard: {
        // backgroundColor applied in component for theme support
        borderRadius: 12,
        borderWidth: 1,
        overflow: 'hidden', // ensure image covers card without spilling
    },
    ventureImage: {
        // image fills the entire card; border radius comes from card
    },
    venturesGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        columnGap: 12,
        rowGap: 16,
        justifyContent: 'flex-start',
        alignContent: 'flex-start',
    },
    // Error state styles
    errorContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 24,
        gap: 16,
    },
    errorTitle: {
        textAlign: 'center',
        marginTop: 8,
    },
    errorMessage: {
        textAlign: 'center',
        marginBottom: 8,
    },
    retryButton: {
        marginTop: 8,
        minWidth: 120,
        alignSelf: 'center',
    },
    floatingButtonContainer: {
        position: 'absolute',
        bottom: 24,
        zIndex: 99,
    },
    reservationSummaryContent: {
        gap: 16,
        borderWidth: 1,
        borderRadius: 16,
        padding: 16,
    },
    reservationSummaryTitle: {
        textAlign: 'left',
    },
    summaryRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
    },
    summaryAmountRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    appointmentActionRow: {
        flexDirection: 'row',
        alignItems: 'center',
        height: 48,
        gap: 12,
    },
    sheetPayNowButton: {
        flex: 1,
        height: 48,
        borderRadius: 4,
        justifyContent: 'center',
        alignItems: 'center',
    },
    sheetPayNowText: {
        color: '#FFFFFF',
        fontFamily: 'Matter-SemiBold',
        fontWeight: '600',
        fontSize: 16,
        lineHeight: 16,
        letterSpacing: 0,
        textAlign: 'center',
    },
    appointmentRescheduleButton: {
        flex: 1,
        height: 48,
        borderRadius: 4,
        borderWidth: 2,
        borderColor: '#1A1A1A',
        justifyContent: 'center',
        alignItems: 'center',
    },
    appointmentRescheduleText: {
        color: '#1A1A1A',
        fontFamily: 'Matter-SemiBold',
        fontWeight: '600',
        fontSize: 16,
        lineHeight: 16,
        letterSpacing: 0,
        textAlign: 'center',
    },
});

export const HomeScreenMobile = HomeScreenMobileAdhaMobile;
export default HomeScreenMobileAdhaMobile;
